import "App.css";
import { ControllerHelper, DefaultController, WikiController } from "collaboration-service";
import TestUCTS from "components/CategorySelection/TestUCTS";
import { getColor, imgIconDefs, ImgThemeProvider, styled, Theme } from 'imaginarity-react-ui';
import { applicationTheme } from 'index';
import * as _ from "lodash";
import React, { Suspense } from 'react';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import { register } from "serviceWorkerRegistration";
import { AppRouteNames, manipulateRoutePrefix, ThisAppEntry, ViewRouteNames } from "services/AppEntryDefinitions";
import { Actions } from "services/ApplicationState/Actions";
import { MasterLayoutModule } from "services/ApplicationState/MasterLayout";
import { AppEntry } from "services/ApplicationState/MasterLayoutRouter";
import { PluginRegistry } from "services/ApplicationState/PluginRegistry";
import { PluginActions } from "services/ApplicationState/PluginStateHandling";
import { routeNames } from 'services/Config';
import { GlobalStyle } from 'services/GlobalStyle';
import HandleAppFocus from "services/HandleAppFocus";
import { AccessHelpers, getFrontEndSettingFromState, initPostDownloadDto, setFrontEndSetting } from 'services/Helpers';
import { getBrowser } from 'services/Helpers/BrowserHelper';
import { createCheckWhereIAm, gotoNamedRoute } from "services/Helpers/RoutingHelper";
import PageStateConnection from "services/PageStateConnection";
import { QandAModel, QANDA_MODELS } from "views/QandA/QandAModels";
import ManageCategoryLike, { CategoryLikeTypes } from "views/Admin/ManageCategoryLike";
import { SsoAuthenticationFailedEntry } from 'views/AuthenticationFailed';
import { cmsConfig } from "views/CMS/CMSConfig";
//import { updateCMSSearchOnRoute } from "views/CMS/useCMSSearch/useCMSSearchHelpers";
import MobileHelper from "services/MobileHelper";
import AdminStart from "views/Admin/AdminStart";
import PDFFileEntryPlaylistView from "views/CMS/FileEntryPlaylistViews/PDFFileEntryPlaylistView";
import ContentFeedDetailPlaylistView from "views/ContentPage/ContentFeedDetailPlaylistView";
import DataPrivacy from "views/DataPrivacy/DataPrivacy";
import DataPrivacySidebar from "views/DataPrivacy/DataPrivacySidebar";
import { FallbackAppEntry } from "views/Fallback/Fallback";
import { GoToExternal } from 'views/GoToExternal';
import { HistoryViewAppEntry, HistoryViewExport } from 'views/History/HistoryView';
import MyHomeStartGLPR from "views/MyHome/MyHomeStartGLPR";
import MyHomeStartJRR from "views/MyHome/MyHomeStartJRR";
import MyHomeStartMC from "views/MyHome/MyHomeStartMC";
import MyHomeStartPRC from "views/MyHome/MyHomeStartPRC";
import MyHomeStartUDD from "views/MyHome/MyHomeStartUDD";
import PlaylistContentInteraction from "views/Playlist/PlaylistContentInteraction";
import PlaylistDetail from "views/Playlist/PlaylistDetail";
import PlaylistOverview from "views/Playlist/PlaylistOverview";
import PodcastAudio from "views/Podcast/PodcastAudio";
import PCPlaylistView from "views/Podcast/PodcastAudioPlaylistView";
import PodcastFeed from "views/Podcast/PodcastFeed";
import PodcastLinkedVideo from "views/Podcast/PodcastLinkedVideo";
import Redirect from "views/Redirect";
import { ResetPasswordAppEntry } from "views/ResetPassword";
import { tubeTheme } from 'views/Tube/TubeMain';
import TubeVideoPlaylistView from "views/Tube/TubeVideoPlaylistView";
import { wikiTheme } from "views/Wiki/WikiTheme";
import AppStart from './components/AppStart';
import { ApplicationState, WikiConfig } from "./services/ApplicationState/ApplicationState";
import { reducer } from './services/ApplicationState/Reducer';
import { ImgI18N, useImgI18N } from "./services/ImgI18N";
import { showNewQandAMenuGroup, showUnpublishedQandAInMenu } from "views/QandA/QAndAHomeConfig";

const GroupManagement = React.lazy(() => import("views/Admin/GroupManagement"));
const GroupMembersManagement = React.lazy(() => import("views/Admin/GroupMembersManagement"));
const NewGroup = React.lazy(() => import("views/Admin/NewGroup"));
const QandAMailConfig = React.lazy(() => import("views/QandA/QandAMailConfig"));
const QAndAHome = React.lazy(() => import("views/QandA/QandAHome"));
const QandAComingSoon = React.lazy(() => import("views/QandA/QandAComingSoon"));
const QandAIncomeQuestions = React.lazy(() => import("views/QandA/QandAIncomeQuestions"));
const MyHomeLearningPath = React.lazy(() => import("views/MyHome/MyHomeLearningPath"));
const CategoryEditSidebarInteractionButtons = React.lazy(() => import("components/PostEdit/TypeImplementations/CategoryEditSidebarInteractionButtons"));
const UserSidebarInteractionButtons = React.lazy(() => import("components/User/UserSidebarInteractionButtons"));
const CatTree = React.lazy(() => import("views/Admin/CatTree"));
const EditCategoryLike = React.lazy(() => import("views/Admin/EditCategoryLike"));
const EditCiaNews = React.lazy(() => import("views/Admin/EditCiaNews"));
const EditMail = React.lazy(() => import("views/Admin/EditMail"));
const EditPost = React.lazy(() => import("views/Admin/EditPost"));
const GroupMails = React.lazy(() => import("views/Admin/GroupMails"));
const MailsInteraction = React.lazy(() => import("views/Admin/MailsInteraction"));
const BestPracticeSidebarInteractionButtons = React.lazy(() => import("views/BestPractice/BestPracticeSidebarInteractionButtons"));
const ChatSidebarInteractionButtons = React.lazy(() => import("views/Chat/ChatSidebarInteractionButtons"));
const CMSArchive = React.lazy(() => import('views/CMS/CMSArchive'));
const CMSAssignedTasks = React.lazy(() => import("views/CMS/CMSAssignedTasks"));
const CMSContent = React.lazy(() => import("views/CMS/CMSContent"));
const CMSRole = React.lazy(() => import("views/CMS/CMSRole"));
const CMSSidebarInteractionButtons = React.lazy(() => import("views/CMS/CMSSidebarInteractionButtons"));
const CMSWorkflow = React.lazy(() => import("views/CMS/CMSWorkflow"));
const CMSContentFilter = React.lazy(() => import("views/CMS/Filter/CMSContentFilter"));
const CmsSidebarFilter = React.lazy(() => import("views/CMS/Filter/CmsSidebarFilter"));
const FeedSidebarInteractionButtons = React.lazy(() => import("views/Feed/FeedSidebarInteractionButtons"));
// const MyHomeSidebarButtons = React.lazy(() => import("views/MyHome/MyHomeSidebarButtons"));
const AddNews = React.lazy(() => import("views/News/AddNews"));
const PlaylistContentInfo = React.lazy(() => import("views/Playlist/PlaylistContentInfo"));
const PlaylistSidebarInteractionButtons = React.lazy(() => import("views/Playlist/PlaylistSidebarInteractionButtons"));
const SlideEdit = React.lazy(() => import("views/SlideShow/SlideEdit"));
const SlideEditContentInteraction = React.lazy(() => import("views/SlideShow/SlideEditContentInteraction"));
const Slides = React.lazy(() => import("views/SlideShow/Slides"));
const Slideshow = React.lazy(() => import("views/SlideShow/Slideshow"));
const SlideshowAccessTokens = React.lazy(() => import("views/SlideShow/SlideshowAccessTokens"));
const SlideshowEdit = React.lazy(() => import("views/SlideShow/SlideshowEdit"));
const SlideshowContentInteraction = React.lazy(() => import("views/SlideShow/SlideshowContentInteraction"));
const SlideshowGroupPosts = React.lazy(() => import("views/SlideShow/SlideshowGroupPosts"));
const SlideshowInteraction = React.lazy(() => import("views/SlideShow/SlideshowInteraction"));
const SlideshowPostInfoButtons = React.lazy(() => import("views/SlideShow/SlideshowPostInfoButtons"));
const SlideshowSettings = React.lazy(() => import("views/SlideShow/SlideshowSettings"));
const WikiCopyGroup = React.lazy(() => import("views/Wiki/WikiCopyGroup"));
const WikiSidebarInteractionButtons = React.lazy(() => import("views/Wiki/WikiSidebarInteractionButtons"));
const WikiTranslationImport = React.lazy(() => import('views/Wiki/WikiTranslationImport'));
const WikiTranslationSend = React.lazy(() => import('views/Wiki/WikiTranslationSend'));
const WikiTranslationSidebarButtons = React.lazy(() => import('views/Wiki/WikiTranslationSidebarButtons'));
const GroupWorkflows = React.lazy(() => import("views/Workflow/GroupWorkflows"));
const GETest = React.lazy(() => import("components/PostEdit/Test/GETest"));
const OpenPost = React.lazy(() => import("views/OpenPost/OpenPost"));
const PorscheMomentAdd = React.lazy(() => import('components/Posts/PorscheMomentPost/PorscheMomentAdd'));
const PorscheMomentView = React.lazy(() => import('components/Posts/PorscheMomentPost/PorscheMomentView'));
const TubeUpload = React.lazy(() => import("components/Tube/TubeUpload"));
const PodcastUpload = React.lazy(() => import("components/Podcast/PodcastUpload"));
const TubeGroupPosts = React.lazy(() => import("components/GroupPosts/TubeGroupPosts"));
const ContentGroupPosts = React.lazy(() => import("components/GroupPosts/ContentGroupPosts"));
const CertificateGroupPosts = React.lazy(() => import("components/GroupPosts/CertificateGroupPosts"));
const PodcastGroupPosts = React.lazy(() => import("components/GroupPosts/PodcastGroupPosts"));
const UserManagement = React.lazy(() => import("views/Admin/UserManagement"));
const UserMerge = React.lazy(() => import("views/Admin/UserMerge"));
const AdminSendMail = React.lazy(() => import("views/Admin/AdminSendMail"));
const NewUser = React.lazy(() => import("views/Admin/NewUser"));
const PPNUserCheck = React.lazy(() => import("views/Admin/PPNUserCheck"));
const TranslateNameSpaces = React.lazy(() => import("views/Admin/TranslateNameSpaces"));
const TubeUploadInteraction = React.lazy(() => import("components/Tube/TubeUploadInteraction"));
const PodcastUploadInteraction = React.lazy(() => import("components/Podcast/PodcastUploadInteraction"));
const Greeting = React.lazy(() => import('components/WelcomeRoad/Greeting'));
const TermsOfUse = React.lazy(() => import('components/WelcomeRoad/TermsOfUse'));
const WikiNewsAdd = React.lazy(() => import('components/Wiki/WikiNewsAdd'));
const GraphDemo = React.lazy(() => import('Dummies/GraphDemo'));
const PlAssign = React.lazy(() => import('Dummies/PLAssign'));
const Search = React.lazy(() => import('layout/parts/Search'));
const Div100vh = React.lazy(() => import("react-div-100vh"));
const AdminDemo = React.lazy(() => import('views/Admin/AdminDemo'));
const PAGStats = React.lazy(() => import('views/PAGStats/PAGStats'));
const BestPracticeAdd = React.lazy(() => import('views/BestPractice/BestPracticeAdd'));
const BestPracticeContentInfo = React.lazy(() => import('views/BestPractice/BestPracticeContentInfo'));
const BestPracticeDetail = React.lazy(() => import('views/BestPractice/BestPracticeDetail'));
const BestPracticeFilter = React.lazy(() => import('views/BestPractice/BestPracticeFilter'));
const BestPracticeMain = React.lazy(() => import('views/BestPractice/BestPracticeMain'));
const Chat = React.lazy(() => import('views/Chat/Chat'));
const ChatAdd = React.lazy(() => import('views/Chat/ChatAdd'));
const ChatContentInfo = React.lazy(() => import('views/Chat/ChatContentInfo'));
const ChatContentInteraction = React.lazy(() => import('views/Chat/ChatContentInteraction'));
const ChatMain = React.lazy(() => import('views/Chat/ChatMain'));
const CMS = React.lazy(() => import("views/CMS/CMS"));
const CMSContentInteraction = React.lazy(() => import("views/CMS/CMSContentInteraction"));
const CMSDirectory = React.lazy(() => import("views/CMS/CMSDirectory"));
const CMSTranslationBatches = React.lazy(() => import("views/CMS/CMSTranslationBatches"));
const CMSFile = React.lazy(() => import("views/CMS/CMSFile"));
const CMSProject = React.lazy(() => import("views/CMS/EditProject/CMSProject"));
const CMSRoles = React.lazy(() => import("views/CMS/CMSRoles"));
const CMSSidebarTreeSelect = React.lazy(() => import("views/CMS/CMSSidebarTreeSelect"));
const CMSTemplate = React.lazy(() => import("views/CMS/CMSTemplate"));
const CMSTemplates = React.lazy(() => import("views/CMS/CMSTemplates"));
const ContentFeed = React.lazy(() => import('views/ContentPage/TaycanPCM'));
// const ContentFeedDetail = React.lazy(() => import('views/ContentPage/Con tentFeedDetail'));
// const ContentFilter = React.lazy(() => import('views/ContentPage/ContentFilter'));
const MyHomeContentFilter = React.lazy(() => import('views/MyHome/MyHomeContentFilter'));
const Academy = React.lazy(() => import('views/ExternalApplications/Academy'));
const Smicro = React.lazy(() => import('views/ExternalApplications/Smicro'));
const PcnaMobileAcademy = React.lazy(() => import('views/ExternalApplications/PcnaMobileAcademy'));
const Faq = React.lazy(() => import('views/Faq/Faq'));
const Inbox = React.lazy(() => import('views/Inbox/InboxHome'));

//const Feed = React.lazy(() => import('views/Feed/Feed'));
const Feed = React.lazy(() => import('views/Feed/Feed'));
// const AppDownload = React.lazy(() => import('views/AppDownload/AppDownload'));
// const FeedFilterAllOld = React.lazy(() => import('views/Feed/FeedFilterAllOld'));
const FeedFilterAll = React.lazy(() => import('views/Feed/FeedFilterAll'));
const JoinView = React.lazy(() => import('views/JoinView/JoinView'));
const MyHomeHomeV3 = React.lazy(() => import('views/MyHome/MyHomeHomeV3'));

const PLaylistEditMain = React.lazy(() => import('views/Playlist/Edit/PLaylistEditMain'));
const CreateGameSinglePlayer = React.lazy(() => import('views/Quiz/CreateGameSinglePlayer'));
const QuizDetails = React.lazy(() => import('views/Quiz/QuizDetails'));
const QuizFilterSorting = React.lazy(() => import('views/Quiz/QuizFilterSorting'));
const QuizGame = React.lazy(() => import('views/Quiz/QuizGame'));
const QuizHome = React.lazy(() => import('views/Quiz/QuizHome'));
const QuizInvite = React.lazy(() => import('views/Quiz/QuizInvite'));
const QuizJoin = React.lazy(() => import('views/Quiz/QuizJoin'));
const QuizSelector = React.lazy(() => import('views/Quiz/QuizSelector'));
const ScormLegalNotes = React.lazy(() => import('views/Scorm/ScormLegalNotes'));
const ScormOverview = React.lazy(() => import('views/Scorm/ScormOverview'));
const ScormPlayer = React.lazy(() => import('views/Scorm/ScormPlayer'));
const GlobalSearchFilters = React.lazy(() => import('views/Search/GlobalSearchFilters'));
const GlobalSearchResults = React.lazy(() => import('views/Search/GlobalSearchResults'));
const Support = React.lazy(() => import('views/Support/Support'));
const ThePage = React.lazy(() => import("views/ThePage/ThePage"));
const ThePageContentInteraction = React.lazy(() => import('views/ThePage/ThePageContentInteraction'));
const ThePageFilter = React.lazy(() => import('views/ThePage/ThePageFilter'));
const TubeFeed = React.lazy(() => import('views/Tube/TubeFeed'));
const TubeLinkedVideo = React.lazy(() => import('views/Tube/TubeLinkedVideo'));
const TubePostInfoButtons = React.lazy(() => import('views/Tube/TubePostInfoButtons'));
const PodcastPostInfoButtons = React.lazy(() => import('views/Podcast/PodcastPostInfoButtons'));
const TubePostInfoUpload = React.lazy(() => import('views/Tube/TubePostInfoUpload'));
const TubeVideo = React.lazy(() => import('views/Tube/TubeVideo'));
const WikiArticle = React.lazy(() => import('views/Wiki/WikiArticle'));
const WikiCategory = React.lazy(() => import('views/Wiki/WikiCategory'));
const WikiContentInfo = React.lazy(() => import('views/Wiki/WikiContentInfo'));
const WikiContentInteraction = React.lazy(() => import('views/Wiki/WikiContentInteraction'));
const WikiEditArticle = React.lazy(() => import('views/Wiki/WikiEditArticle'));
const WikiFaq = React.lazy(() => import('views/Wiki/WikiFaq'));
const WikiNewArticle = React.lazy(() => import('views/Wiki/WikiNewArticle'));
// const UserProfileContentInteraction = React.lazy(() => import('./components/User/UserProfileContentInteraction'));
const UserStatus = React.lazy(() => import('./components/User/UserStatus'));
const WelcomeRoad = React.lazy(() => import('./components/WelcomeRoad/WelcomeRoad'));
const DemosMain = React.lazy(() => import("./Dummies/DemosMain"));
const CoreBackendTest = React.lazy(() => import("./Dummies/CoreBackendTest"));
const MasterLayout = React.lazy(() => import("./layout/MasterLayout"));
const MasterLayoutRouter = React.lazy(() => import('./layout/MasterLayoutRouter'));
const ActivityFeed = React.lazy(() => import('./views/Feed/ActivityFeed'));
const ManagePlaylists = React.lazy(() => import('./views/Playlist/ManagePlaylists'));
// const PlaylistHome = React.lazy(() => import('./views/Playlist/PlaylistHome'));
const User = React.lazy(() => import('./views/User/User'));
const UserBookmarks = React.lazy(() => import("./views/User/UserBookmarks"));


const WikiHome = React.lazy(() => import('./views/Wiki/WikiHome'));
const WikiReview = React.lazy(() => import('./views/Wiki/WikiReview'));
const WikiReviewerOverview = React.lazy(() => import('./views/Wiki/WikiReviewerOverview'));

const AssetManagement = React.lazy(() => import('./views/AssetManagement/AssetManagement'));
//const AssetManagement = React.lazy(() => import('./views/AssetManagement/AddTubeAsset'));
const AssetContentInteraction = React.lazy(() => import('./views/AssetManagement/AssetContentInteraction'));
const AssetAdd = React.lazy(() => import('./views/AssetManagement/AssetAdd'));
const PlaylistAssignment = React.lazy(() => import('./views/Playlist/PlaylistAssignment'));

const QuizGroupPosts = React.lazy(() => import("views/Quiz/Admin/QuizGroupPosts"));
const QuizGroupInteraction = React.lazy(() => import("views/Quiz/Admin/QuizGroupInteraction"));
const ContentGroupInteraction = React.lazy(() => import("views/Quiz/Admin/ContentGroupInteraction"));
const QuizAndQuestionEdit = React.lazy(() => import("views/Quiz/Admin/QuizAndQuestionEdit"));

const MyHomeSidebarInteractionButtons = React.lazy(() => import("views/MyHome/MyHomeSidebarInteractionButtons"));
const VideoFileEntryPlaylistView = React.lazy(() => import("views/CMS/FileEntryPlaylistViews/VideoFileEntryPlaylistView"));
const ImageFileEntryPlaylistView = React.lazy(() => import("views/CMS/FileEntryPlaylistViews/ImageFileEntryPlaylistView"));
const PlaylistHasWork = React.lazy(() => import("views/Playlist/PlaylistHasWork"));

const SessionAdminCreateEdit = React.lazy(() => import("views/SessionManagement/SessionAdminCreateEdit"));
const SessionAdminInteraction = React.lazy(() => import("views/SessionManagement/SessionAdminInteraction"));
const SessionAdminSessionType = React.lazy(() => import("views/SessionManagement/SessionAdminSessionType"));
const SessionAdminInteractionButtons = React.lazy(() => import("views/SessionManagement/SessionAdminInteractionButtons"));
const SessionAdminTree = React.lazy(() => import("views/SessionManagement/SessionAdminTree"));
const RolesView = React.lazy(() => import("views/Admin/RolesView"));
const RoleView = React.lazy(() => import("views/Admin/RoleView"));
const SessionAdminResourceType = React.lazy(() => import("views/SessionManagement/SessionAdminResourceType"));
const SessionAdminResources = React.lazy(() => import("views/SessionManagement/SessionAdminResources"));
const SessionResourceFilter = React.lazy(() => import("views/SessionManagement/SessionResourceFilter"));
const SessionAdminInformation = React.lazy(() => import("views/SessionManagement/SessionAdminInformation"));
const SessionAdminOverview = React.lazy(() => import("views/SessionManagement/SessionAdminOverview"));
const SessionLearnerOverview = React.lazy(() => import("views/SessionManagement/SessionLearnerOverview"));
const SessionLearnerEnrollment = React.lazy(() => import("views/SessionManagement/SessionLearnerEnrollment"));
const SessionAdminEnrollments = React.lazy(() => import("views/SessionManagement/SessionAdminEnrollments"));


const x = false;

/*
const WaitingComponent = (Component: any, Loader: any) => {
  return (props: any) => (
    <Suspense fallback={<Loader />}>
      <Component {...props} />
    </Suspense>
  );
}

const Loader = () => <div>Loading!</div>;


const Feed = WaitingComponent(lazy(() => import("views/Feed/Feed")), Loader);
const SlideMain = WaitingComponent(lazy(() => import("./Dummies/SlideMain")), Loader);
const SlideWithId = WaitingComponent(lazy(() => import("./Dummies/SlideWithId")), Loader);

const DummyFeed = WaitingComponent(lazy(() => import("./Dummies/DummyFeed")), Loader);
const DummySMW = WaitingComponent(lazy(() => import("./Dummies/DummySMW")), Loader);
*/

const composeEnhancers = composeWithDevTools({
  // options like actionSanitizer, stateSanitizer
});

export const store = createStore(
  reducer, /* preloadedState, */ composeEnhancers(
    applyMiddleware(thunk)),
);



// QandA CONFIG

interface AppProps {

}
interface AppState {
  colors: boolean;
  progress: number;
  msg: string;
  loadingDone: boolean;
  modules?: MasterLayoutModule[];
  showInfo: boolean;
}
export const IEWarning = styled.div`
  position: fixed;
    width: 100%;
    margin: auto;
    background: ${props => getColor(props, "@accentRed")};
    color: #FFF;
    font-size:0.8rem;
    line-height:32px;
    height:32px; 
    bottom:0px;
    text-align: center;
    cursor: row-resize;
    border-top:2px solid ${props => getColor(props, "@accentRed")};
    &:hover{
      background: ${props => getColor(props, "@darkGrey")};
      border-top:2px solid ${props => getColor(props, "@accentRed")};
    }
`;
export const availableForApplication = (appName: string) => (s: ApplicationState) => s.currentAppEntry?.name === appName;
export const availableForRouteName = (appName: AppRouteNames, viewName?: ViewRouteNames) => (s: ApplicationState) => s.currentAppEntry?.routeName === appName && (viewName === undefined || viewName === s.currentAppView?.routeName);
export const availableForNotInSubView = (appName: AppRouteNames) => (s: ApplicationState) => s.currentAppEntry?.routeName === appName && undefined === s.currentAppView;
export const availableForAppAndSubViews = (appName: AppRouteNames, routeNames: ViewRouteNames[]) => (s: ApplicationState) => s.currentAppEntry?.routeName === appName && (s.currentAppView === undefined || s.currentAppView.routeName === undefined || _.find(routeNames, r => r === s.currentAppView?.routeName) !== undefined);
export const availableOr = (...orMethods: Array<(s: ApplicationState) => boolean>) => (s: ApplicationState) => {
  for (let i = 0; i < orMethods.length; i++)
    if (orMethods[i](s))
      return true;
  return false;
}

const a1: MasterLayoutModule[] =
  [
    // Deactivated for the time being until it has been clarified whether only PPE playlists should be displayed or none at all
    // {
    //   icon: "playlist",
    //   name: "Your playlists Wiki",
    //   moduleRenderer: (p) => <WikiPlaylistContentInfo />,
    //   available: (s: ApplicationState) => {
    //     // if (s?.currentAppEntry?.name === "BestPractice" || s?.currentAppEntry?.name === "myhome")
    //     //   return false;
    //     //console.log('s?.currentAppEntry?.name', s?.currentAppEntry?.name);

    //     if (s?.currentAppEntry?.name === 'Wiki')
    //       return true;
    //     return false;

    //     // if (s?.currentAppEntry?.name === "Wiki") {
    //     //   const pco = s.playlistsState?.playlistStates?.length ?? 0;
    //     //   const tco = s.playlistsState?.playlistStatesTopic?.length ?? 0;
    //     //   console.log('now is here', (pco + tco) > 0);
    //     //   return (pco + tco) > 0;
    //     // }
    //     // else 
    //     //   return false;
    //   },
    //   type: "SearchAndFilter",
    //   noAccordion: false,
    //   open: true
    // },


    {
      icon: "cog",
      name: "PPX Campus Modules",
      moduleRenderer: (p) => <div />,
      type: "ContentInteraction",
      available: availableForRouteName("myhome"),
      noAccordion: true,
      open: true,
    },



    {
      icon: "link",
      name: "site navigation",
      moduleRenderer: (p) => <TubePostInfoButtons {...p} />,
      //available: availableFor("Tube"),
      available: availableForAppAndSubViews("tube", ['tube_cat', 'tube_video_details',]),
      type: "UserInteraction",
      noAccordion: true,
      open: true,
    },
    {
      icon: "link",
      name: "site navigation1",
      moduleRenderer: (p) => <PodcastPostInfoButtons {...p} />,
      //available: availableFor("Tube"),
      available: availableForAppAndSubViews("podcast", ['podcast_cat', 'podcast_audio_details',]),
      type: "UserInteraction",
      noAccordion: true,
      open: true,
    },

    {
      icon: "link",
      name: "slide navigation",
      moduleRenderer: (p) => <SlideshowPostInfoButtons {...p} />,
      available: availableForRouteName('slideshow'),
      type: "UserInteraction",
      noAccordion: true,
      open: true,
    },

    {
      icon: "upload",
      name: "Upload",
      moduleRenderer: (p) => <TubePostInfoUpload {...p} />,
      available: () => tubeTheme.showUploadForm, //availableFor(tubeSettings.showUploadForm ? "Tube" : ""),
      type: "ContentContext",
      noAccordion: false,
    },





    {
      icon: "cog",
      name: "select project",
      moduleRenderer: (p) => <CMSSidebarTreeSelect />,
      available: availableForRouteName("cms"),
      type: "SearchAndFilter",
      noAccordion: true,
      open: true
    },



    {
      icon: "info",
      name: "MyHome-Interaction",
      moduleRenderer: (p) => <MyHomeSidebarInteractionButtons />,
      available: availableForRouteName("myhome"),
      type: "ContentInteraction",
      noAccordion: true,
    },

    {
      icon: "info",
      name: "CMS-SB-Interaction-Buttons",
      moduleRenderer: (p) => <CMSSidebarInteractionButtons />,
      // available: () => true,
      available: availableForRouteName("cms"),
      type: "ContentInteraction",
      noAccordion: true,
    },

    {
      icon: "info",
      name: "CMS-Interaction",
      moduleRenderer: (p) => <CMSContentInteraction />,
      available: availableForRouteName("cms"),
      type: "ContentInteraction",
      noAccordion: true,
    },
    {
      icon: "cog",
      name: "CMS-Content",
      moduleRenderer: (p) => <CMSContentFilter />,
      available: availableForRouteName("cms", "cms_content"),
      type: "SearchAndFilter",
      noAccordion: true,
      open: true
    },
    {
      icon: "filter",
      name: "CMS-SB-Search-Filter",
      moduleRenderer: (p) => <CmsSidebarFilter />,
      available: availableForNotInSubView("cms"),
      // available: availableOr(availableForNotInSubView("cms"), availableForRouteName("cms", "cms_dir")),
      type: "SearchAndFilter",
      noAccordion: true,
    },

    {
      icon: "info",
      name: "CMS-Assigned-Tasks",
      moduleRenderer: (p) => cmsConfig.showAssignedTasksOnSidebar ? <CMSAssignedTasks /> : <div />,
      available: availableForRouteName("cms"),
      type: "SearchAndFilter",
      noAccordion: true,
    },

    // {
    //   icon: "info",
    //   name: "ToDos on CMS",
    //   moduleRenderer: (p) => <ToDos toDoItems={[
    //     { responsible: "Edwin", task: "Homesite: Suchbegriff auch in Dateinamen (Bsp. Test)" },
    //     { responsible: "Sascha", task: "Homesite: add content type to search results in case it is not file type", },
    //     { responsible: "Alexey", task: "Templates: Bei Klick auf neues Template sollte der Edit Mode für dieses neue Template direkt geöffnet sein", done: true },
    //     { responsible: "Alexey", task: "Projectinfo: Es fehlt das Projektbild und die Members mit ihren Rollen", done: true },
    //     { responsible: "Alexey", task: "Fileview: Das Setzen der Kategorien nicht sofort ändern, erst nach Klick auf Save, X bricht Vorgang ab", done: true },
    //   ]} />,
    //   available: availableFor("CMS"),
    //   type: "ContentContext",
    //   noAccordion: true,
    // },

    {
      icon: "info",
      name: "PPE-SB-Interaction-Buttons",
      moduleRenderer: (p) => <WikiSidebarInteractionButtons />,
      available: availableForApplication("Wiki"),
      type: "ContentInteraction",
      noAccordion: true,
    },



    {
      icon: "language",
      name: "Translation",
      moduleRenderer: (p) => <WikiTranslationSidebarButtons />,
      // available: availableForRouteName("wiki"),
      available: availableForAppAndSubViews("wiki", ["wiki_article", "wiki_category"]),
      // type: "ContentInfo",
      type: "ContentInteraction",
      noAccordion: true,
    },






    {
      icon: "info",
      name: "PPE-Interaction",
      moduleRenderer: (p) => <WikiContentInteraction />,
      available: availableForApplication("Wiki"),
      type: "ContentInteraction",
      noAccordion: true,
    },

    {
      icon: "info",
      name: "ThePage-Interaction",
      moduleRenderer: (p) => <ThePageContentInteraction />,
      available: availableForRouteName("PALMS"),
      type: "ContentInteraction",
      noAccordion: true,
    },
    {
      icon: "filter",
      name: "Filter",
      moduleRenderer: (p) => <ThePageFilter />,
      available: (s: ApplicationState) => !s.thePageState.detailId && s.currentAppEntry?.name === "Academy",
      type: "SearchAndFilter",
      noAccordion: true,
      open: true
    },
    {
      icon: "info",
      name: "BestPractice-Info",
      moduleRenderer: (p) => <BestPracticeContentInfo />,
      available: availableForRouteName("best", "best_detail"),
      type: "ContentInfo",
      noAccordion: true,
    },

    {
      icon: "info",
      name: "BestPractice-Interaction-Buttons",
      moduleRenderer: (p) => <BestPracticeSidebarInteractionButtons />,
      available: availableForRouteName("best"),
      type: "ContentInteraction",
      noAccordion: true,
    },

    {
      icon: "chat",
      name: "Chats-Info",
      moduleRenderer: (p) => <ChatContentInfo />,
      available: availableForRouteName("chat"),
      type: "ContentInfo",
      noAccordion: true,
    },
    {
      icon: "info",
      name: "Chat-Interaction-Buttons",
      moduleRenderer: (p) => <ChatSidebarInteractionButtons />,
      available: availableForRouteName("chat"),
      type: "ContentInteraction",
      noAccordion: true,
    },
    {
      icon: "info",
      name: "Chat-Interaction",
      moduleRenderer: (p) => <ChatContentInteraction />,
      available: availableForRouteName("chat"),
      type: "ContentInteraction",
      noAccordion: true,
    },

    {
      icon: "info",
      name: "User-Interaction-Buttons",
      moduleRenderer: (p) => <UserSidebarInteractionButtons {...p} />,
      available: availableForRouteName("user"),
      type: "ContentInteraction",
      noAccordion: true,
    },
    // {
    //   icon: "info",
    //   name: "User-Interaction",
    //   moduleRenderer: (p) => <UserProfileContentInteraction {...p} />,
    //   available: availableFor("User"),
    //   type: "ContentInteraction",
    //   noAccordion: true,
    // },
    {
      icon: "info",
      name: "PPE-Info",
      moduleRenderer: (p) => <WikiContentInfo />,
      available: availableForApplication("Wiki"),
      type: "ContentInfo",
      noAccordion: true,
    },
    {
      icon: "info",
      name: "PL-Interaction-Buttons",
      moduleRenderer: (p) => <PlaylistSidebarInteractionButtons />,
      available: availableForRouteName("playlist"),
      type: "ContentInteraction",
      noAccordion: true,
    },



















    {
      icon: "info",
      name: "PL-Interaction",
      moduleRenderer: (p) => <DataPrivacySidebar />,
      available: availableForRouteName("dataPrivacy"),
      type: "ContentInfo",
      noAccordion: true,
    },




    {
      icon: "info",
      name: "PL-Interaction",
      moduleRenderer: (p) => <PlaylistContentInteraction />,
      available: availableForRouteName("playlist"),
      type: "ContentInteraction",
      noAccordion: true,
    },












    {
      icon: "playlist",
      name: "Your playlists",
      moduleRenderer: (p) => <PlaylistContentInfo />,
      available: availableForRouteName('playlist'),
      // available: (s: ApplicationState) => {
      //   // if (s?.currentAppEntry?.name === "BestPractice" || s?.currentAppEntry?.name === "myhome")
      //   //   return false;
      //   if (s?.currentAppEntry?.name === "Playlist") {
      //     const pco = s.playlistsState?.playlistStates?.length ?? 0;
      //     const tco = s.playlistsState?.playlistStatesTopic?.length ?? 0;
      //     return (pco + tco) > 0;
      //   }
      //   else
      //     return false;
      // },
      type: "ContentInteraction",
      noAccordion: true,
      open: true
    },









    {
      icon: "filter",
      name: "BestPractice-filter",
      moduleRenderer: (p) => <BestPracticeFilter />,
      available: availableForNotInSubView("best"),
      type: "SearchAndFilter",
      open: true,
      noAccordion: true,
    },
    {
      icon: "filter",
      name: "Search-filter",
      moduleRenderer: (p) => <GlobalSearchFilters />,
      available: availableForNotInSubView("search"),
      type: "SearchAndFilter",
      noAccordion: true,
      open: true
    },
    // {
    //   icon: "activities",
    //   name: "activities",
    //   moduleRenderer: (p) => <ActivityFeed />,
    //   // available: availableForNotInSubView("user"),
    //   available: s => (availableForNotInSubView("user")(s) && window.location.hostname !== "porsche-retail-impulse.com"),
    //   type: "ContentInfo",
    //   noAccordion: false,
    //   open: true
    // },
    {
      icon: "filter",
      name: "Quiz-filter",
      moduleRenderer: (p) => <QuizFilterSorting />,
      //available: availableForNotInSubView("Quiz"),
      available: availableForAppAndSubViews("quiz", ["quiz_details"]),
      type: "SearchAndFilter",
      noAccordion: true,
      open: true
    },

    // {
    //   icon: "filter",
    //   name: "Content-filter",
    //   moduleRenderer: (p) => <ContentFilter />,
    //   available: availableForRouteName("content"),
    //   // availableFor
    //   // availableForNotInSubView
    //   type: "SearchAndFilter",
    //   noAccordion: true,
    //   open: true
    // },

    {
      icon: "filter",
      name: "MyHome-Content-filter",
      moduleRenderer: (p) => <MyHomeContentFilter />,
      available: availableForRouteName("myhome"),
      type: "SearchAndFilter",
      noAccordion: true,
      open: true
    },

    {
      icon: "feed",
      name: "Community-Filter",
      moduleRenderer: (p) => <FeedFilterAll />,
      available: availableForAppAndSubViews("community", ["community_feed", "community_feed_trending"]),
      type: "SearchAndFilter",
      noAccordion: true,
      open: true
    },
    {
      icon: "feed",
      name: "Community-Interaction",
      moduleRenderer: (p) => <FeedSidebarInteractionButtons />,
      available: availableForRouteName("community"),
      type: "ContentInteraction",
      noAccordion: true,
    },
    // {
    //   icon: "activities",
    //   name: "useractivities",
    //   moduleRenderer: (p) => <ActivityFeed />,
    //   available: availableForRouteName("community"),
    //   type: "ContentInfo",
    //   noAccordion: false,
    //   open: false
    // },
    {
      icon: "user",
      name: "Assetmanagement-Interaction",
      moduleRenderer: (p) => <AssetContentInteraction />,
      available: availableForRouteName("assets"),
      type: "ContentInteraction",
      noAccordion: true,
    },
    {
      icon: "video player",
      name: "TubeContent-Interaction",
      moduleRenderer: (p) => <TubeUploadInteraction />,
      type: "ContentInteraction",
      noAccordion: true,
      available: availableOr(availableForRouteName("admin", "admin_tube_upload"), availableForRouteName("admin", "admin_tube_group")),
    },
    {
      icon: "audio",
      name: "PodcastContent-Interaction",
      moduleRenderer: (p) => <PodcastUploadInteraction />,
      type: "ContentInteraction",
      noAccordion: true,
      available: availableOr(availableForRouteName("admin", "admin_podcast_upload"), availableForRouteName("admin", "admin_podcast_group")),
    },
    {
      icon: "slideshow",
      name: "Slideshow-Interaction",
      moduleRenderer: (p) => <SlideshowInteraction />,
      type: "ContentInteraction",
      noAccordion: true,
      available: availableOr(availableForRouteName("admin", "admin_slideshow_upload"), availableForRouteName("admin", "admin_slideshow_group")),
    },
    {
      icon: "slideshow",
      name: "SlideshowSettings-Interaction",
      moduleRenderer: (p) => <SlideshowSettings />,
      type: "ContentInteraction",
      noAccordion: true,
      available: availableForRouteName('slideshow', 'admin_slideshow_upload'),
    },
    {
      icon: "slideshow",
      name: "SlideshowAccessTokens-Interaction",
      moduleRenderer: (p) => <SlideshowContentInteraction />,
      type: "ContentInteraction",
      noAccordion: true,
      available: availableForRouteName('slideshow', 'slideshow_access_tokens'),
    },
    {
      icon: "slideshow",
      name: "SlideEditContentInteraction-Interaction",
      moduleRenderer: (p) => <SlideEditContentInteraction />,
      type: "ContentInteraction",
      noAccordion: true,
      available: availableForRouteName('slideshow', 'slideshow_slide_edit'),
    },

    {
      icon: "mail",
      name: "Mails-Interaction",
      moduleRenderer: (p) => <MailsInteraction />,
      type: "ContentInteraction",
      noAccordion: true,
      available: availableOr(availableForRouteName("admin", "admin_group_mails"), availableForRouteName("admin", "admin_edit_mail")),
    },
    {
      icon: "edit",
      name: "Category-Edit-Interaction-Buttons",
      moduleRenderer: (p) => <CategoryEditSidebarInteractionButtons />,
      available: availableForRouteName("admin", "admin_edit_category_like"),
      type: "ContentInteraction",
      noAccordion: true,
    },



    {
      icon: "questionark",
      name: "Quiz-Interaction",
      moduleRenderer: (p) => <QuizGroupInteraction />,
      type: "ContentInteraction",
      noAccordion: true,
      available: availableForRouteName("admin", "admin_quiz_group"),
    },
    {
      icon: "questionark",
      name: "Content-Interaction",
      moduleRenderer: (p) => <ContentGroupInteraction />,
      type: "ContentInteraction",
      noAccordion: true,
      available: availableForRouteName("admin", "admin_content_group"),
    },

    {
      icon: "add",
      name: "SessionInteractionButtons",
      moduleRenderer: (p) => <SessionAdminInteractionButtons />,
      type: "ContentInteraction",
      noAccordion: true,
      available: availableForRouteName("session")
    },

    {
      icon: "add",
      name: "SessionInteraction",
      moduleRenderer: (p) => <SessionAdminInteraction />,
      type: "ContentInteraction",
      noAccordion: true,
      available: availableForRouteName("session")
    },

    {
      icon: "add",
      name: "SessionAdminTree",
      moduleRenderer: (p) => <SessionAdminTree />,
      type: "SearchAndFilter",
      noAccordion: true,
      available: availableOr(availableForRouteName("session", "session_add"), availableForRouteName("session", "session_admin_overview"))
    },
    {
      icon: "filter",
      name: "SessionResourceFilter",
      moduleRenderer: (p) => <SessionResourceFilter />,
      available: availableForRouteName("session", "sessionResources"),
      type: "SearchAndFilter",
      noAccordion: true,
      open: true
    }
  ];

const feed = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  icon: "feed",
  name: "Community",
  startPriorityWeight: 2,
  contentRenderer: () => <Redirect from="community" to="community_feed" />,
  title: "application::community",
  titleStyle: {
    //fontStyle: "italic",
    //fontWeight: 600,
  },
  hideInMenu: frontEndConfig.minimizeMenu_feed,

  banner: "https://cdn.imaginarity.com/public/thepage/myhome/momentsScreen.png",
  route: "community",
  routeName: "community",
  left: {
    manageOpen: {
      get: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("community_feed"))
          return s.feedState.rightSidebar ?? false;
        return getFrontEndSettingFromState<boolean>(s.user, "adminLeftSidebar") ?? false;
      },
      toggle: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("community_feed"))
          store.dispatch(Actions.setFeedRightSidebar(!s.feedState.rightSidebar));
        else
          await setFrontEndSetting<boolean>(s.user, "adminLeftSidebar", (c) => !c);
      }
    }
  },

  right: {
    hidden: true,
  },
  accessCheck: AccessHelpers.isInGroup(["EXCITE", "COMMUNITY"]),
  themeManipulation: (t: Theme) => ({
    ...t,
    mainArea: {
      ...t.mainArea,
      backgroundColor: t.applicationTheming.feedTheme?.backgroundColor ?? t.mainArea.backgroundColor,
      color: t.applicationTheming.feedTheme.color ?? t.mainArea.color,
    }
  }),
  views: [
    {
      render: () => <Feed />,
      route: "feed/:tokenId?",
      routeName: "community_feed"
    },
    {
      render: () => <Feed trending />,
      route: "trending/:tokenId?",
      routeName: "community_feed_trending"
    },
    {
      route: "add/:id?",
      render: () => <PorscheMomentAdd />,
      routeName: "community_add_post",
    },
    {
      route: "view/:id",
      render: () => <PorscheMomentView />,
      routeName: "community_view_post"
    },
    {
      route: "activities",
      routeName: "community_activities",
      render: () => <ActivityFeed />,
    }
  ]
})
// const appDownload = (): ThisAppEntry => ({
//   icon: "download",
//   name: "AppDownload",
//   startPriorityWeight: 2,
//   contentRenderer: () => <AppDownload />,
//   title: "application::native app",
//   left: {
//     hidden: true,
//   },
//   right: {
//     hidden: true
//   },
//   titleStyle: {
//     //fontStyle: "italic",
//     //fontWeight: 600,
//   },
//   route: "app",
//   routeName: "app_download",
//   accessCheck: () => { const b = getBrowser(); return b.os === "iOS" && b.name !== "ios-webview"; }
// })


const quiz = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Quiz",
  startPriorityWeight: 2,
  title: "application::quiz",
  icon: "games",
  routeName: "quiz",
  route: "quiz",
  noLogin: false,
  hidden: false,
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/quizScreen.png",
  hideInMenu: frontEndConfig.minimizeMenu_quiz,
  themeManipulation: (t: Theme) => ({
    ...t,
    mainArea: {
      ...t.mainArea,
      backgroundColor: t.applicationTheming.quizTheme?.backgroundColor ?? t.mainArea.backgroundColor,
      color: t.applicationTheming.quizTheme.color ?? t.mainArea.color,
    }
  }),
  accessCheck: AccessHelpers.isInGroup("QUIZ"),

  right: {
    hidden: true,
  },

  contentRenderer: () => <QuizHome />,
  views: [
    {
      route: ":id",
      routeName: "quiz_game",
      render: () => <QuizGame />
    },
    {
      route: "single/:quizId/:mode?/:context?",
      routeName: "quiz_singleplayer",
      render: () => <CreateGameSinglePlayer />
    },
    {
      route: "multi/:quizId/:mode?",
      routeName: "quiz_multiplayer",
      render: () => <QuizInvite />
    },
    {
      route: "join/:gameId",
      routeName: "quiz_join",
      render: () => <QuizJoin />
    },
    {
      route: "selector/:quizId",
      routeName: "quiz_selector",
      render: () => <QuizSelector />
    },
    {
      route: "detail/:quizId",
      routeName: "quiz_details",
      render: () => <QuizDetails />
    },
  ]
})

const smirco = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "iTrainer",
  startPriorityWeight: 1,
  titleStyle: {
    textTransform: "unset",
  },
  icon: "education",
  route: "itrainer",
  routeName: "smicro",
  title: "application::smicro",
  hideInMenu: frontEndConfig.minimizeMenu_smicro,

  banner: "https://cdn.imaginarity.com/public/thepage/myhome/itrainerScreen.png",
  contentRenderer: () => <Smicro />,
  accessCheck: AccessHelpers.isInGroup(["SSOAPP:smicro"]),
  left: {
    hidden: true,
  },
  right: {
    hidden: true,
  },
})

const myhome = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 6,
  name: "myhome",
  icon: "home",
  route: "myhome/:ppnId?",
  noLogin: false,
  routeName: "myhome",
  hidden: false,
  title: "myhome::myhome",
  hideInMenu: frontEndConfig.minimizeMenu_myhome,
  contentRenderer: () => <MyHomeHomeV3 />,
  left: {
    hidden: true,
  },
  right: {
    hidden: true,
  },
  views: [
    {
      route: "news/:id?",
      routeName: "myhome_news",
      render: () => <AddNews
        namespace="myhome"
        namedRoute="myhome"
        methodDelete={WikiController.DeleteWikiNews}
        methodPatch={WikiController.PatchWikiNews}
        methodPost={WikiController.PostWikiNews}
      />
    },
    {
      route: "learningPath/:id?/:filter?",
      routeName: "learningPath",
      render: () => <MyHomeLearningPath />
    },
    {
      route: "myhome_prc",
      routeName: "myhome_prc",
      render: () => <MyHomeStartPRC />
    },
    {
      route: "myhome_udd",
      routeName: "myhome_udd",
      render: () => <MyHomeStartUDD />
    },
    {
      route: "myhome_mc",
      routeName: "myhome_mc",
      render: () => <MyHomeStartMC />
    },
    {
      route: "myhome_jrr",
      routeName: "myhome_jrr",
      render: () => <MyHomeStartJRR />
    },
    {
      route: "myhome_reports",
      routeName: "myhome_reports",
      render: () => <MyHomeStartGLPR />
    },
  ],
})

const dataPrivacy = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 6,
  name: "dataPrivacy",
  icon: "data privacy",
  route: "dataPrivacy",
  noLogin: false,
  routeName: "dataPrivacy",
  hidden: true,
  title: "dataPrivacy::dataPrivacy",
  hideInMenu: frontEndConfig.minimizeMenu_dataPrivacy,
  contentRenderer: () => <DataPrivacy />,
  left: {
    hidden: false,
  },
  right: {
    hidden: true,
  },
})


const academy = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "academy",
  icon: "award",
  route: "academy",
  title: "application::academy",
  routeName: "PALMS",
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/academyScreen.png",
  hideInMenu: frontEndConfig.minimizeMenu_palms,

  contentRenderer: () => <Academy />,
  left: {
    hidden: true,
  },
  right: {
    hidden: true,
  },
})


const pcnaMobileAcademy = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "pcnaMobileAcademy",
  startPriorityWeight: 1,
  icon: "education",
  route: "pcnaMobileAcademy",
  title: "application::pcnaMobileAcademy",
  routeName: "pcnaMobileAcademy",
  // banner: "https://cdn.imaginarity.com/public/thepage/myhome/pcnaScreen.png",
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/pcnaScreen2.png",
  contentRenderer: () => <PcnaMobileAcademy />,
  hideInMenu: frontEndConfig.minimizeMenu_pcnaMobileAcademy,

  accessCheck: AccessHelpers.isInGroup(["SSOAPP:PCNA-Mobile-Academy"]),
  // accessCheck: AccessHelpers.isInGroup(["SSOAPP:smicro"]),
  left: {
    hidden: true,
  },
  right: {
    hidden: true,
  },
})

const support = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "support",
  icon: "zoom in",
  route: "support",
  title: "application::support",
  routeName: "support",
  contentRenderer: () => <Support />,
  accessCheck: AccessHelpers.isInGroup("SUPPORT"),
  hideInMenu: frontEndConfig.minimizeMenu_support,
  left: {
    hidden: true,
  },
  right: {
    hidden: true,
  },
})

const openPost = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "OpenPost",
  icon: "content aggregation",
  route: "open/:id",
  title: "application::post edit",
  noLogin: false,
  routeName: "open_post",
  hidden: true,
  hideInMenu: frontEndConfig.minimizeMenu_openPost,
  contentRenderer: () => <OpenPost />,
})

const peTest = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "PostEdit",
  icon: "users",
  route: "postEdit/:id?/:isCat?",
  title: "application::post edit",
  noLogin: false,
  routeName: "peTest",
  hideInMenu: frontEndConfig.minimizeMenu_peTest,
  contentRenderer: () => <GETest />,
})

const welcome = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Welcome",
  icon: "user",
  route: "welcome/:stepid/:token",
  title: "application::welcome",
  noLogin: true,
  hidden: true,
  renderDirectly: true,
  routeName: "welcome",
  hideInMenu: frontEndConfig.minimizeMenu_welcome,

  contentRenderer: () => <WelcomeRoad />,
  left: {
    hidden: true,
  },
  right: {
    hidden: true,
  },
})

const termsOfUse = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Terms of Use",
  icon: "user",
  route: "tou",
  title: "application::termsofuse",
  noLogin: true,
  hidden: true,
  renderDirectly: true,
  routeName: "termsOfUse",
  contentRenderer: () => <TermsOfUse />,
  hideInMenu: frontEndConfig.minimizeMenu_termsOfUse,
  left: {
    hidden: true,
  },
  right: {
    hidden: true,
  },
})


const greeting = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Greeting",
  icon: "user",
  route: "greeting",
  routeName: "greeting",
  title: "application::greeting",
  noLogin: true,
  hidden: true,
  renderDirectly: true,
  contentRenderer: () => <Greeting />,
  hideInMenu: frontEndConfig.minimizeMenu_greeting,
  left: {
    hidden: true,
  },
  right: {
    hidden: true,
  },
})

const joinView = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Join",
  icon: "user",
  route: "join/:itemid/:p1/:p2?/:p3?/:p4?/:p5?/:p6?/:p7?/:p8?/:p9?/:p10?/:p11?/:p12?/:p13?/:p14?/:p15?",
  routeName: "join",
  title: "application::join",
  hidden: true,
  renderDirectly: true,
  contentRenderer: () => <JoinView />,
  hideInMenu: frontEndConfig.minimizeMenu_joinView,
  left: {
    hidden: true,
  },
  right: {
    hidden: true,
  },
})

const demos = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Demos",
  icon: "info brain",
  route: "demos", ///:name?",
  title: "application::demos",
  hidden: false,
  noLogin: false,
  routeName: "demos",
  contentRenderer: () => <DemosMain />,
  accessCheck: AccessHelpers.isGlobalAdmin(),
  hideInMenu: frontEndConfig.minimizeMenu_demos,

  right: {
    hidden: true,
  },
  left: {
    hidden: true,
  },
  views: [
    {
      route: "graph",
      routeName: "demos_graph",
      render: () => <GraphDemo />
    },
    {
      route: "plassign",
      routeName: "demos_plassign",
      render: () => <PlAssign />
    },
    {
      route: "coreBackendTest",
      routeName: "demos_coreBackendTest",
      render: () => <CoreBackendTest />
    },
    {
      route: ":name",
      routeName: "demos_demo", // + nr,
      render: () => <DemosMain />
    }
  ]
})

const playlist = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Playlist",
  icon: "playlist",
  // route: "playlist/:type?/:id?",
  title: "application::playlist",
  route: "playlist",
  routeName: "playlist",
  hidden: false,
  // HERE
  hideInMenu: frontEndConfig.minimizeMenu_playlist,
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/playlistScreen.png",
  noLogin: false,
  right: {
    // hidden: true
    manageOpen: {
      get: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("playlist_edit") || c("playlist_manage") || c("playlist_podcast_audio_details") || c("playlist_detail") || c("playlist_assign"))
          return s.playlistsState.rightSidebar ?? false;
        return getFrontEndSettingFromState<boolean>(s.user, "playlistRightSidebar") ?? false;
      },
      toggle: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("playlist_edit") || c("playlist_manage") || c("playlist_podcast_audio_details") || c("playlist_detail") || c("playlist_assign"))
          store.dispatch(Actions.setPlaylistRightSidebar(!s.playlistsState.rightSidebar));
        else
          await setFrontEndSetting<boolean>(s.user, "playlistRightSidebar", (c) => !c);
      }
    }
  },
  left: {
    hidden: true
    // manageOpen: {
    //   get: async () => {
    //     const s = store.getState();
    //     const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
    //     if (c("playlist_manage") || c("playlist") || c("playlist_detail") || c("playlist_assign"))
    //       return s.playlistsState.leftSidebar ?? false;
    //     if (c("playlist_edit"))
    //       return s.playlistsState.leftSidebar ?? true;
    //     return getFrontEndSettingFromState<boolean>(s.user, "playlistLeftSidebar") ?? false;
    //   },
    //   toggle: async () => {
    //     const s = store.getState();
    //     const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
    //     if (c("playlist_edit") || c("playlist_manage") || c("playlist") || c("playlist_detail") || c("playlist_assign"))
    //       store.dispatch(Actions.setPlaylistLeftSidebar(!s.playlistsState.leftSidebar));
    //     else
    //       await setFrontEndSetting<boolean>(s.user, "playlistLeftSidebar", (c) => !c);
    //   }
    // }
  },
  accessCheck: AccessHelpers.isInGroup("WIKI"),
  contentRenderer: () => <PlaylistOverview />,

  views: [
    {
      route: "editPlaylist/:id?/:copy?",
      routeName: "playlist_edit",
      render: () => <PLaylistEditMain />,
    },
    {
      route: "showPlaylistFiltered/:filter?",
      routeName: "playlist_filtered",
      render: () => <PlaylistOverview />
    },
    {
      route: "playlistDetail/:id?/:contextId?",
      routeName: "playlist_detail",
      render: () => <PlaylistDetail />
    },
    {
      route: "managePlaylists",
      routeName: "playlist_manage",
      render: () => <ManagePlaylists />
    },
    {
      route: "assignPlaylist/:id/:tab?",
      routeName: "playlist_assign",
      render: () => <PlaylistAssignment />
    },
    {
      route: "content/art/:context/:id/:catid?/:catid1?/:catid2?/:catid3?/:catid4?/:catid5?",
      routeName: "playlist_content_art",
      themeManipulation: (t: Theme) => ({
        ...t,
        mainArea: {
          ...t.mainArea,
          backgroundColor: t.applicationTheming.contentTheme?.backgroundColor ?? t.mainArea.backgroundColor,
          color: t.applicationTheming.contentTheme.color ?? t.mainArea.color,
        },
      }),
      render: () => <ContentFeedDetailPlaylistView />
    },
    {
      route: "video/:context/:id/:catid?/:catid1?/:catid2?/:catid3?/:catid4?/:catid5?",
      routeName: "playlist_tube_video_details",
      themeManipulation: (t: Theme) => ({
        ...t,
        mainArea: {
          ...t.mainArea,
          backgroundColor: t.applicationTheming.tubeTheme?.backgroundColor ?? t.mainArea.backgroundColor,
          color: t.applicationTheming.tubeTheme.color ?? t.mainArea.color,
        }
      }),
      render: () => <TubeVideoPlaylistView />
    },
    {
      route: "video_fe/:context/:id/:catid?/:catid1?/:catid2?/:catid3?/:catid4?/:catid5?",
      routeName: "playlist_fileentry_video_details",
      themeManipulation: (t: Theme) => ({
        ...t,
        mainArea: {
          ...t.mainArea,
          backgroundColor: t.applicationTheming.tubeTheme?.backgroundColor ?? t.mainArea.backgroundColor,
          color: t.applicationTheming.tubeTheme.color ?? t.mainArea.color,
        }
      }),
      render: () => <VideoFileEntryPlaylistView />
    },
    {
      route: "pdf_fe/:context/:id",
      routeName: "playlist_fileentry_pdf",
      render: () => <PDFFileEntryPlaylistView />
    },
    {
      route: "image_fe/:context/:id",
      routeName: "playlist_fileentry_image",
      render: () => <ImageFileEntryPlaylistView />
    },
    {
      route: "podcast_fe/:context/:id",
      routeName: "playlist_fileentry_podcast",
      themeManipulation: (t: Theme) => ({
        ...t,
        mainArea: {
          ...t.mainArea,
          backgroundColor: t.applicationTheming.podcastTheme?.backgroundColor ?? t.mainArea.backgroundColor,
          color: t.applicationTheming.podcastTheme.color ?? t.mainArea.color,
        }
      }),
      render: () => <VideoFileEntryPlaylistView />
    },
    {
      route: "audio/:context/:id/:catid?/:catid1?/:catid2?/:catid3?/:catid4?/:catid5?",
      routeName: "playlist_podcast_audio_details",
      themeManipulation: (t: Theme) => ({
        ...t,
        mainArea: {
          ...t.mainArea,
          backgroundColor: t.applicationTheming.tubeTheme?.backgroundColor ?? t.mainArea.backgroundColor,
          color: t.applicationTheming.tubeTheme.color ?? t.mainArea.color,
        }
      }),
      render: () => <PCPlaylistView />
    },
    {
      route: "hasWork",
      routeName: "playlist_has_work",
      render: () => <PlaylistHasWork />
    },

  ]
})

const slide = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 1,
  name: "Slides",
  icon: "slideshow",
  route: "slideshow",
  routeName: "slideshow",
  title: "application::slideshow",
  // banner: "https://cdn.imaginarity.com/public/images/PAG_X.svg",
  hideInMenu: frontEndConfig.minimizeMenu_slideshow,
  noLogin: false,
  hidden: false,
  left: {
    hidden: false,
  },
  right: {
    // hidden: false,
    manageOpen: {
      get: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("admin_slideshow_upload") || c("slideshow_slide_edit"))
          return s.slideshowState.rightSidebar ?? true;
        else
          return getFrontEndSettingFromState<boolean>(s.user, "slideshowRightSidebar") ?? false;
      },
      toggle: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("admin_slideshow_upload") || c("slideshow_slide_edit"))
          store.dispatch(Actions.setSlideShowRightSidebar(!s.slideshowState.rightSidebar));
        else
          await setFrontEndSetting<boolean>(s.user, "slideshowRightSidebar", (c) => !c);
      }
    }
  },
  accessCheck: AccessHelpers.isInGroup("SLIDESHOW"),
  contentRenderer: () => <Slides />,
  views: [
    {
      route: "cat/:catid?/:catid1?/:catid2?/:catid3?/:catid4?/:catid5?",
      routeName: "slideshow_cat",
      render: () => <Slides />,
    },
    {
      route: "slideshow/:groupid/:id?",
      routeName: "admin_slideshow_upload",
      render: () => <SlideshowEdit />
    },
    {
      route: "access/:id",
      routeName: "slideshow_access_tokens",
      render: () => <SlideshowAccessTokens />
    },
    {
      route: "slide/:id/:tokenid?",
      routeName: "slideshow_slide",
      render: () => <Slideshow />
    },
    {
      route: "edit/:slideshowId/:slideId/:templateId?",
      routeName: "slideshow_slide_edit",
      render: () => <SlideEdit />
    },
  ]
})

const tube = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Tube",
  startPriorityWeight: 3,
  icon: "video player",
  noLogin: false,
  route: "tube",
  routeName: "tube",
  title: "application::tube",
  hidden: false,
  hideInMenu: frontEndConfig.minimizeMenu_tube,
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/tubeScreen.png",
  accessCheck: AccessHelpers.isInGroup("TUBE"),
  themeManipulation: (t: Theme) => ({
    ...t,
    mainArea: {
      ...t.mainArea,
      backgroundColor: t.applicationTheming.tubeTheme?.backgroundColor ?? t.mainArea.backgroundColor,
      color: t.applicationTheming.tubeTheme.color ?? t.mainArea.color,
    }
  }),
  right: {
    hidden: true,
  },
  contentRenderer: () => <TubeFeed />,
  views: [
    {
      route: "cat/:catid?/:catid1?/:catid2?/:catid3?/:catid4?/:catid5?",
      routeName: "tube_cat",
      render: () => <TubeFeed />,
    },
    {
      noLogin: true,
      renderDirectly: true,
      routeName: "tube_public",
      route: "video_public/:id",
      render: () => <TubeLinkedVideo publicAccess />
    },
    {
      renderDirectly: true,
      routeName: "tube_private",
      route: "video_private/:id",
      render: () => <TubeLinkedVideo />
    },
    {
      route: "video/:id/:catid?/:catid1?/:catid2?/:catid3?/:catid4?/:catid5?",
      routeName: "tube_video_details",
      render: () => <TubeVideo />
    },
    {
      route: "upload",
      routeName: "tube_mass_upload",
      render: () => <TubeUpload />
    }
  ]
})

const podcast = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Podcast",
  startPriorityWeight: 3,
  icon: "play circle",
  noLogin: false,
  route: "podcast",
  routeName: "podcast",
  title: "application::podcast",
  titleStyle: {
    textTransform: "capitalize",
  },
  hidden: false,
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/podcastScreen.png",
  hideInMenu: AccessHelpers.isAdminInGroup("PODCAST") ? false : frontEndConfig.minimizeMenu_podcast,
  accessCheck: AccessHelpers.isInGroup("PODCAST"),
  themeManipulation: (t: Theme) => ({
    ...t,
    mainArea: {
      ...t.mainArea,
      backgroundColor: t.applicationTheming.podcastTheme?.backgroundColor ?? t.mainArea.backgroundColor,
      color: t.applicationTheming.podcastTheme.color ?? t.mainArea.color,
    }
  }),
  right: {
    hidden: true,
  },
  contentRenderer: () => <PodcastFeed />,
  views: [
    {
      route: "cat/:catid?/:catid1?/:catid2?/:catid3?/:catid4?/:catid5?",
      routeName: "podcast_cat",
      render: () => <PodcastFeed />,
    },
    {
      noLogin: true,
      renderDirectly: true,
      routeName: "podcast_public",
      route: "audio_public/:id",
      render: () => <PodcastLinkedVideo publicAccess />
    },
    {
      renderDirectly: true,
      routeName: "podcast_private",
      route: "audio_private/:id",
      render: () => <PodcastLinkedVideo />
    },
    {
      route: "audio/:id/:catid?/:catid1?/:catid2?/:catid3?/:catid4?/:catid5?",
      routeName: "podcast_audio_details",
      render: () => <PodcastAudio />
    },
    {
      route: "upload",
      routeName: "podcast_mass_upload",
      render: () => <TubeUpload />
    }
  ]
})

const content = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 4,
  name: "Content",
  icon: "content aggregation",
  noLogin: false,
  route: "content",
  routeName: "content",
  title: "application::content",
  hideInMenu: frontEndConfig.minimizeMenu_smw,
  hidden: false,
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/smwScreen.png",
  accessCheck: AccessHelpers.isInGroup("LANDING PAGE SMART MOBILITY"),
  themeManipulation: (t: Theme) => ({
    ...t,
    mainArea: {
      ...t.mainArea,
      backgroundColor: t.applicationTheming.contentTheme?.backgroundColor ?? t.mainArea.backgroundColor,
      color: t.applicationTheming.contentTheme.color ?? t.mainArea.color,
      padding: "0px",
    }
  }),
  right: {
    hidden: true,
  },
  left: {
    hidden: true
  },

  contentRenderer: () => <ContentFeed />,
  views: []
})

const scorm = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 4,
  name: "Scorm",
  icon: "education",
  noLogin: false,
  route: "scorm",
  routeName: "scorm",
  title: "application::scorm",
  hidden: false,
  noPadding: true,
  hideInMenu: frontEndConfig.minimizeMenu_scorm,
  accessCheck: AccessHelpers.isInGroup("SCORM"),
  right: {
    hidden: true,
  },
  left: {
    hidden: true,
  },
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/scormScreen.png",
  contentRenderer: () => <ScormOverview />,
  views: [
    {
      route: "legals",
      routeName: "scorm_legals",
      render: () => <ScormLegalNotes />,
    },
    {
      route: "player/:token/:id",
      routeName: "scorm_player",
      render: () => <ScormPlayer />,
    },
  ]
})

const assetManagement = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Assets",
  icon: "playlist",
  noLogin: false,
  route: "assets",
  routeName: "assets",
  title: "application::asset management",
  hidden: false,
  left: {
    hidden: false,
  },
  right: {
    hidden: false,
  },
  hideInMenu: frontEndConfig.minimizeMenu_assetManagement,
  contentRenderer: () => <AssetManagement />,
  views: [
    {
      route: "add",
      routeName: "assets_add",
      render: () => <AssetAdd />
    }
  ]
})

const pagStats = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "PAG Stats",
  icon: "admin",
  noLogin: false,
  route: "pag_stats",
  routeName: "pag_stats",
  title: "application::pag stats",
  hidden: false,
  order: 10000,
  accessCheck: AccessHelpers.isAdminSomewhere(),
  hideInMenu: frontEndConfig.minimizeMenu_pagStats,
  left: {
    hidden: true
  },
  right: {
    hidden: true,
  },
  contentRenderer: () => <PAGStats />,
});

const admin = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Admin",
  icon: "admin",
  noLogin: false,
  route: "admin",
  routeName: "admin",
  title: "application::admin area",
  hidden: false,
  order: 10000,
  accessCheck: AccessHelpers.isAdminSomewhere(),
  hideInMenu: frontEndConfig.minimizeMenu_admin,
  right: {
    hidden: false
  },
  // right: {
  //   hidden: false,
  // },
  left: {
    manageOpen: {
      get: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("admin_tube_upload"))
          return s.adminState.leftSidebar ?? false;
        return getFrontEndSettingFromState<boolean>(s.user, "adminLeftSidebar") ?? false;
      },
      toggle: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("admin_tube_upload"))
          store.dispatch(Actions.adminSetLeftSidebar(!s.adminState.leftSidebar));
        else
          await setFrontEndSetting<boolean>(s.user, "adminLeftSidebar", (c) => !c);
      }
    }
  },
  contentRenderer: () => <AdminStart />,
  views: [
    {
      route: "test/utcs",
      routeName: "testUCTS",
      render: () => <TestUCTS />,
    },
    {
      route: "tube/upload/:groupid/:id?",
      routeName: "admin_tube_upload",
      render: () => <TubeUpload />,
    },
    {
      route: "podcast/upload/:groupid/:id?",
      routeName: "admin_podcast_upload",
      render: () => <PodcastUpload />,
    },
    {
      route: "tube/group/:groupId?",
      routeName: "admin_tube_group",
      render: () => <TubeGroupPosts editRouteName="admin_tube_upload" />,
    },
    {
      route: "content/group/:groupId?",
      routeName: "admin_content_group",
      render: () => <ContentGroupPosts editRouteName="admin_content_edit" />,
    },
    {
      route: "content/edit/:postType/:groupid/:id?",
      routeName: "admin_content_edit",
      render: () => <EditPost initPost={() => initPostDownloadDto("", "ContentPost") as any} />,
    },
    {
      route: "certificates/group/:groupId?",
      routeName: "admin_certificate_group",
      render: () => <CertificateGroupPosts editRouteName="admin_edit_post" editRouteParams={{ postType: "LearnerCertificate" }} />,
    },
    {
      route: "podcast/group/:groupId?",
      routeName: "admin_podcast_group",
      render: () => <PodcastGroupPosts editRouteName="admin_podcast_upload" />,
    },
    {
      route: "slides/group/:groupId?",
      routeName: "admin_slideshow_group",
      render: () => <SlideshowGroupPosts editRouteName="admin_slideshow_upload" />,
    },
    {
      route: "quiz/group/:groupId?",
      routeName: "admin_quiz_group",
      render: () => <QuizGroupPosts editRouteName="admin_quiz_and_question_edit" />,
    },
    {
      route: "quiz/edit/:groupid?/:id?",
      routeName: "admin_quiz_and_question_edit",
      render: () => <QuizAndQuestionEdit />
    },
    {
      route: "old",
      routeName: "admin_old",
      render: () => <AdminDemo />,
    },
    {
      route: "user/:id?",
      routeName: "admin_user",
      render: () => <UserManagement />,
      //accessCheck: () => false,
      accessCheck: AccessHelpers.isGlobalAdmin(),

    },
    {
      route: "user_merge/:id?",
      routeName: "admin_user_merge",
      render: () => <UserMerge />,
      //accessCheck: () => false,
      accessCheck: AccessHelpers.isGlobalAdmin(),

    },
    {
      route: "mail/:id?",
      routeName: "admin_send_mail",
      render: () => <AdminSendMail />,
      //accessCheck: () => false,
      accessCheck: AccessHelpers.isGlobalAdmin(),

    },
    {
      route: "newUser",
      routeName: "admin_newUser",
      render: () => <NewUser />,
      //accessCheck: () => false,
      accessCheck: AccessHelpers.isGlobalAdmin(),

    },
    {
      route: "user/ppn/check",
      routeName: "admin_user_ppn_check",
      render: () => <PPNUserCheck />,
      //accessCheck: () => false,
      accessCheck: AccessHelpers.isGlobalAdmin(),

    },
    {
      route: "translate",
      routeName: "admin_translateNS",
      render: () => <TranslateNameSpaces />,
      //accessCheck: () => false,
      accessCheck: AccessHelpers.isGlobalAdmin(),

    },
    {
      route: 'editCategoryLike/:kind/:groupId/:id/:path*',
      routeName: "admin_edit_category_like",
      render: () => <EditCategoryLike />,
    },
    {
      route: 'mails/:groupId?',
      routeName: "admin_group_mails",
      render: () => <GroupMails />,
    },
    {
      route: 'mail/edit/:groupId/:id?',
      routeName: "admin_edit_mail",
      render: () => <EditMail />,
    },
    {
      route: 'post/edit/:postType/:groupid/:id?',
      routeName: "admin_edit_post",
      render: () => <EditPost />,
    },
    {
      route: 'cattree/:id?',
      routeName: "admin_cat_tree",
      render: () => <CatTree />,
    },
    {
      route: "categories",
      routeName: "admin_categories",
      render: () => <ManageCategoryLike namespace="admin" homeRoute="admin" />,
    },
    {
      route: "competence",
      routeName: "admin_competences",
      render: () => <ManageCategoryLike namespace="admin" homeRoute="admin" kind={CategoryLikeTypes.Competence} />,
    },
    {
      route: "locations",
      routeName: "admin_locations",
      render: () => <ManageCategoryLike namespace="admin" homeRoute="admin" kind={CategoryLikeTypes.Location} />,
    },
    {
      route: "ciaNews",
      routeName: "admin_ciaNews",
      render: () => <EditCiaNews />
    },
    {
      route: "group/members/:groupId?",
      routeName: "admin_group_members",
      render: () => <GroupMembersManagement />
    },
    {
      route: "group/base/:groupId?",
      routeName: "admin_group_management",
      render: () => <GroupManagement />
    },
    {
      route: "group/new",
      routeName: "admin_newGroup",
      render: () => <NewGroup />,
      accessCheck: AccessHelpers.isAdminSomewhere(),
    }
  ]
})

const best = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 2,
  name: "BestPractice",
  icon: "bulb",
  route: "best",
  routeName: "best",
  title: "application::best practice",
  noLogin: false,
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/priScreen.png",
  hideInMenu: frontEndConfig.minimizeMenu_bestPractice,
  hidden: false,
  accessCheck: AccessHelpers.isInGroup("BEST PRACTICE"),
  contentRenderer: () => <BestPracticeMain />,
  right: {
    manageOpen: {
      get: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("best"))
          return s.bestpracticeState.rightSidebar ?? true;
        return getFrontEndSettingFromState<boolean>(s.user, "bestRightSidebar") ?? true;
      },
      toggle: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("best"))
          store.dispatch(Actions.setBestRightSidebar(!s.bestpracticeState.rightSidebar));
        else
          await setFrontEndSetting<boolean>(s.user, "bestRightSidebar", (c) => !c);
      }
    }
  },
  views: [
    {
      route: "detail/:id",
      routeName: "best_detail",
      render: () => <BestPracticeDetail />,
    },
    {
      route: "add/:id?",
      routeName: "best_add",
      render: () => <BestPracticeAdd />
    },
  ]
})



const chat = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 1,
  name: "Chat",
  icon: "chat",
  route: "chat",
  title: "application::chat",
  routeName: "chat",
  noLogin: false,
  hidden: false,
  hideInMenu: frontEndConfig.minimizeMenu_chat,
  accessCheck: AccessHelpers.isInGroup(["EXCITE", "COMMUNITY"]),
  contentRenderer: () => <ChatMain />,
  views: [
    {
      route: "in/:id",
      routeName: "chat_open",
      render: () => <Chat />
    },
    {
      route: "add",
      routeName: "chat_add",
      render: () => <ChatAdd />
    },
  ]
})

const inbox = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 1,
  name: "Inbox",
  icon: "bell",
  title: "application::inbox",
  route: routeNames.inbox,
  routeName: "inbox",
  noLogin: false,
  hidden: true,
  hideInMenu: frontEndConfig.minimizeMenu_inbox,
  contentRenderer: () => <Inbox />,
  left: {
    hidden: true,
  },
  right: {
    hidden: true,
  },
})



const user = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 1,
  name: "User",
  icon: "user",
  title: "application::user profile",
  route: "user",
  routeName: "user",
  left: {
    hidden: true,
  },
  right: {
    hidden: true,
  },
  noLogin: false,
  hidden: false,
  hideInMenu: frontEndConfig.minimizeMenu_user,
  contentRenderer: () => <User />,
  views: [
    {
      route: "show/:id?",
      routeName: "user_details",
      render: () => <User />
    },
    {
      route: "bookmarks",
      routeName: "user_bookmarks",
      render: () => <UserBookmarks />
      // render: () => <User />
    }]
})

const cms = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 5,
  name: "CMS",
  icon: "cms admin",
  title: "application::cms",
  route: routeNames.cms,
  routeName: "cms",
  noLogin: false,
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/cmsScreen.png",
  hidden: false,
  order: 8000,
  hideInMenu: frontEndConfig.minimizeMenu_cms,

  right: {
    manageOpen: {
      get: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("cms_content") || c("cms_translation_batches") || c("cms_project"))
          return s.cmsState.rightSidebar ?? false;
        return getFrontEndSettingFromState<boolean>(s.user, "cmsRightSidebar") ?? false;
      },
      toggle: async () => {
        const s = store.getState();
        const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
        if (c("cms_content") || c("cms_translation_batches") || c("cms_project"))
          store.dispatch(Actions.setCMSRightSidebar(!s.cmsState.rightSidebar));
        else
          await setFrontEndSetting<boolean>(s.user, "cmsRightSidebar", (c) => !c);
      }
    }
  },
  actions: [
    Actions.setCMSSearchResultsFiles(undefined),
    Actions.setCMSSearchResultsProjects(undefined),
    Actions.setCMSContentSearchResultsFiles(undefined),
    Actions.setCMSContentSearchResultsProjects(undefined),
    // { type: "EXECUTE", value: () => updateCMSSearchOnRoute("", { allowFuzzy: true }, {}, undefined, undefined, "projects", true) },
  ],
  accessCheck: AccessHelpers.isInGroup("CMS"),
  contentRenderer: () => <CMS />,
  views: [
    {
      route: "project/:id?/:copy?",
      routeName: "cms_project",
      render: () => <CMSProject />
    },
    {
      route: routeNames.cmsRoutes.templates,
      routeName: "cms_templates",
      render: () => <CMSTemplates />
    },
    {
      route: routeNames.cmsRoutes.template,
      routeName: "cms_template",
      render: () => <CMSTemplate />
    },
    {
      route: routeNames.cmsRoutes.roles,
      routeName: "cms_roles",
      render: () => <CMSRoles />
    },

    {
      route: routeNames.cmsRoutes.role,
      routeName: "cms_create_edit_role",
      render: () => <CMSRole />,
    },
    {
      route: routeNames.cmsRoutes.directory,
      routeName: "cms_dir",
      render: () => <CMSDirectory />
    },
    {
      route: routeNames.cmsRoutes.translationBatches,
      routeName: "cms_translation_batches",
      render: () => <CMSTranslationBatches />
    },
    {
      route: routeNames.cmsRoutes.file,
      routeName: "cms_file",
      render: () => <CMSFile />
    },
    {
      route: routeNames.cmsRoutes.archive,
      routeName: "cms_archive",
      render: () => <CMSArchive />
    },
    {
      route: routeNames.cmsRoutes.archiveFile,
      routeName: "cms_archive_file",
      render: () => <CMSFile />
    },
    {
      route: routeNames.cmsRoutes.selectWorkflow,
      routeName: "cms_select_workflow",
      render: () => <GroupWorkflows />,
    },
    {
      route: routeNames.cmsRoutes.manageLocations,
      routeName: "cms_locations",
      render: () => <ManageCategoryLike kind={CategoryLikeTypes.Location} namespace="cms" homeRoute="cms" groupType={["CMS"]} />,
    },
    {
      route: routeNames.cmsRoutes.manageCategories,
      routeName: "cms_categories",
      render: () => <ManageCategoryLike namespace="cms" homeRoute="cms" groupType={["CMS"]} />,
    },
    {
      route: routeNames.cmsRoutes.createWorkflow,
      routeName: "cms_create_edit_workflow",
      render: () => <CMSWorkflow />,
    },
    {
      route: routeNames.cmscontent,
      routeName: "cms_content",
      render: () => <CMSContent />
    },
    // {
    //   name: "editCategory",
    //   route: routeNames.cmsRoutes.editCategory,
    //   routeName: "category",
    //   render: () => <CMSEditCategory />,
    // },
    // {
    //   name: "editLocation",
    //   route: routeNames.cmsRoutes.editLocation,
    //   routeName: "location",
    //   render: () => <CMSEditLocation />,
    // },
  ]
})

const cmscontent = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 5,
  name: "CMS-Content",
  icon: "user",
  title: "application::cms",  // show cms-content view if user clicks on CMS button on top bar. "upgrade" due to story 9353, feature 9317
  route: routeNames.cms + '/' + routeNames.cmscontent,
  routeName: "cmscontent",
  noLogin: false,
  hidden: false,
  // hideInSideBar: CurrentSettings.minimizeMenu,
  hideInMenu: frontEndConfig.minimizeMenu_cmscontent,
  contentRenderer: () => <CMSContent />,
  accessCheck: AccessHelpers.isInGroup("CMS-OUTPUT"),
  right: {
    hidden: true,
  },
})



/** One setting per wiki module - InfoHub and Warranty can be used by entirely different people. */
const wikiRightSidebarSetting = (config: WikiConfig) => `${config.baseRoute ?? "wiki"}RightSidebar`;

const isWikiReviewRoute = (s: ApplicationState, config: WikiConfig) => {
  const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
  return c(manipulateRoutePrefix("wiki_reviewer_overview", "wiki", config.baseRoute))
    || c(manipulateRoutePrefix("wiki_admin_review_view", "wiki", config.baseRoute));
};

const wiki = (config: WikiConfig, frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 5,
  name: "Wiki",
  icon: config.icon ?? "wiki",
  title: `application::${config.applicationName ?? "wiki"}`,
  route: config.baseRoute ?? routeNames.wiki,
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/ppeScreen.png",
  routeName: manipulateRoutePrefix("wiki", "wiki", config.baseRoute),
  noLogin: false,
  hidden: false,
  hideInMenu: frontEndConfig.minimizeMenu_wiki,

  right: {
    manageOpen: {
      // The two review overviews need the width for their tables and start collapsed, keeping
      // that choice in the state. Everywhere else the sidebar starts open and the user's choice
      // is persisted per wiki module, because the modules can serve different groups of people.
      get: async () => {
        const s = store.getState();
        if (isWikiReviewRoute(s, config))
          return s.wikiState.rightSidebar ?? false;
        return getFrontEndSettingFromState<boolean>(s.user, wikiRightSidebarSetting(config)) ?? true;
      },
      toggle: async () => {
        const s = store.getState();
        if (isWikiReviewRoute(s, config))
          store.dispatch(Actions.setWikiRightSidebar(!(s.wikiState.rightSidebar ?? false)));
        else
          await setFrontEndSetting<boolean>(s.user, wikiRightSidebarSetting(config), (c) => !(c ?? true));
      }
    }
  },
  // manageOpen: {
  //   get: async () => {
  //     const s = store.getState();
  //     const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
  //     if (c("wiki"))
  //       return s.wikiState.rightSidebar ?? true;
  //     else
  //       return getFrontEndSettingFromState<boolean>(s.user, "wikiRightSidebar") ?? false;

  //   },
  //   toggle: async () => {
  //     const s = store.getState();
  //     const c = createCheckWhereIAm(s.currentAppEntry, s.currentAppView);
  //     if (c("wiki"))
  //       store.dispatch(Actions.setWikiRightSidebar(!s.wikiState.rightSidebar));
  //     else
  //       await setFrontEndSetting<boolean>(s.user, "wikiRightSidebar", (c) => !c);
  //   }
  // }

  left: {
    hidden: true,
    // manageOpen: {
    //   get: async () => {
    //     const s = store.getState();
    //     return getFrontEndSettingFromState<boolean>(s.user, "wikiLeftSidebar") ?? false;
    //   },
    //   toggle: async () => {
    //     const s = store.getState();
    //     await setFrontEndSetting<boolean>(s.user, "wikiLeftSidebar", (c) => !c);
    //   }
    // }
  },
  actions: [
    Actions.setWikiCategories(undefined),
    Actions.setWikiArticle(undefined),
    Actions.setWikiConfig(config),
  ],
  accessCheck: AccessHelpers.isInGroup("WIKI", config.groupAttribute),
  themeManipulation: config.themeManipulation ?? ((t: Theme) => ({
    ...t,
    mainArea: {
      ...t.mainArea,
      backgroundColor: t.applicationTheming.wikiTheme?.backgroundColor ?? t.mainArea.backgroundColor,
      color: t.applicationTheming.wikiTheme.color ?? t.mainArea.color,
    }
  })),
  contentRenderer: () => <WikiHome />,
  views: [
    {
      route: "category/:id/:sid?",
      routeName: manipulateRoutePrefix("wiki_category", "wiki", config.baseRoute),
      render: () => <WikiCategory />
    },
    {
      route: "article/:id/:secId?/:colId?",
      // route: "art",
      routeName: manipulateRoutePrefix("wiki_article", "wiki", config.baseRoute),
      render: () => <WikiArticle />
    },
    {
      route: "new",
      // route: "art",
      routeName: manipulateRoutePrefix("wiki_create_article", "wiki", config.baseRoute),
      render: () => <WikiNewArticle />
    },
    {
      route: "edit/:id?",
      // route: "art",
      routeName: manipulateRoutePrefix("wiki_edit_article", "wiki", config.baseRoute),

      render: () => <WikiEditArticle />
    },
    {
      route: "addNews/:id?",
      routeName: manipulateRoutePrefix("wiki_add_news", "wiki", config.baseRoute),
      render: () => <WikiNewsAdd />
    },
    {
      route: "news/:id?",
      routeName: manipulateRoutePrefix("wiki_news", "wiki", config.baseRoute),
      render: () => <WikiHome />
    },
    {
      route: routeNames.wikiRoutes.faq,
      routeName: manipulateRoutePrefix("wiki_faq", "wiki", config.baseRoute),
      render: () => <WikiFaq />
    },
    {
      route: routeNames.wikiRoutes.translationSend,
      routeName: manipulateRoutePrefix("wiki_translation_send", "wiki", config.baseRoute),
      render: () => <WikiTranslationSend />
    },
    {
      route: routeNames.wikiRoutes.translationImport,
      routeName: manipulateRoutePrefix("wiki_translation_import", "wiki", config.baseRoute),
      render: () => <WikiTranslationImport />
    },
    {
      route: routeNames.wikiRoutes.copyWikiGroup,
      routeName: manipulateRoutePrefix("wiki_copyWikiGroup", "wiki", config.baseRoute),
      render: () => <WikiCopyGroup />
    },
    {
      route: "history/:dataId?",
      routeName: manipulateRoutePrefix("wiki_history", "wiki", config.baseRoute),
      render: () => <HistoryViewExport />
    },
    {
      route: "review/:id?",
      routeName: manipulateRoutePrefix("wiki_admin_review_view", "wiki", config.baseRoute),
      render: () => <WikiReview />
    },
    {
      route: "reviewer-overview/:id?",
      routeName: manipulateRoutePrefix("wiki_reviewer_overview", "wiki", config.baseRoute),
      render: () => <WikiReviewerOverview />
    }


  ]
})

const faq = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "FAQ",
  icon: "info plus",
  title: "application::faq",
  titleStyle: {
    textTransform: "uppercase",
    //fontWeight: 600,
  },
  route: "faq",
  routeName: "faq",
  noLogin: false,
  right: {
    hidden: true
  },
  hideInMenu: frontEndConfig.minimizeMenu_faq,
  hidden: false,
  order: 9000,
  contentRenderer: () => <Faq />,
})

const qanda = (config: WikiConfig, frontEndConfig: Record<string, any>): ThisAppEntry => ({
  icon: config.icon ?? "questionark circle",
  title: `application::${config.applicationName ?? "q&a"}`,
  route: config.baseRoute ?? "qanda",
  banner: "https://cdn.imaginarity.com/public/thepage/myhome/ppeScreen.png",
  routeName: manipulateRoutePrefix("qanda", "qanda", config.baseRoute),
  startPriorityWeight: 1,
  accessCheck: AccessHelpers.isInGroup("WIKI", config.groupAttribute),


  name: "Q&A",
  // banner: "https://cdn.imaginarity.com/public/images/PAG_X.svg",
  noLogin: false,
  hidden: false,
  hideInMenu: frontEndConfig.minimizeMenu_qanda,
  // hideInMenu: false,
  contentRenderer: () => <QAndAHome />,
  // NEW START
  left: {
    hidden: true
  },
  right: {
    hidden: true
  },
  // NEW END
  actions: [
    Actions.setWikiCategories(undefined),
    Actions.setWikiArticle(undefined),
    Actions.setWikiConfig(config),
  ],
  views: [
    {
      route: "edit/:id?",
      routeName: manipulateRoutePrefix("q&a_edit_question", "q&a", config.baseRoute),
      render: () => <WikiEditArticle isFaqItem />
    },
    {
      route: "open/:questionId?/:admin?",
      routeName: manipulateRoutePrefix("q&a_search", "q&a", config.baseRoute),
      render: () => <QAndAHome />
    },

    {
      route: "admin/incomingQuestions/view",
      routeName: manipulateRoutePrefix("q&a_admin_incoming_questions", "q&a", config.baseRoute),
      render: () => <QandAIncomeQuestions />
    },
    {
      route: "admin/submittedQuestions/view",
      routeName: manipulateRoutePrefix("q&a_submitted_questions", "q&a", config.baseRoute),
      render: () => <QandAIncomeQuestions userOnly />
    },
    {
      route: "history/:dataId?",
      routeName: manipulateRoutePrefix("q&a_admin_history", "q&a", config.baseRoute),
      render: () => <HistoryViewExport />
    },
    {
      route: "admin/mail_config",
      routeName: manipulateRoutePrefix("q&a_mail_config", "q&a", config.baseRoute),
      render: () => <QandAMailConfig />
    }
  ]
})

const qandaModelEntry = (model: QandAModel, frontEndConfig: Record<string, any>): ThisAppEntry => {
  const menu = { name: `Q&A ${model.title}`, menuGroup: "general::q&a", menuLabel: model.title };
  if (model.published)
    return {
      ...qanda({
        backgroundImage: model.backgroundImage ?? wikiTheme.faqBackgroundUrl,
        backgroundPosition: model.backgroundPosition,
        groupAttribute: model.groupAttribute,
        baseRoute: model.baseRoute,
        applicationName: model.applicationName,
        // Cayenne Electric behält seinen übersetzten Text, neue Q&As zeigen ihren Namen aus der Liste.
        modelName: model.baseRoute ? model.title : undefined,
      }, frontEndConfig),
      ...menu,
      // Sichtbarkeit im Menü steuert published, nicht die eigene WIKI-Gruppe: Alle Q&A-Nutzer sehen alle veröffentlichten Q&As.
      accessCheck: AccessHelpers.isInGroup("WIKI", "qanda"),
    };
  // Keine echte Q&A-Instanz: Die würde ohne eigene WIKI-Gruppe die Inhalte eines anderen Q&A anzeigen.
  return {
    ...menu,
    icon: "questionark circle",
    title: "general::q&a",
    route: model.baseRoute ?? model.id,
    routeName: (model.baseRoute ?? model.id) as AppRouteNames,
    accessCheck: AccessHelpers.isInGroup("WIKI", "qanda"),
    noLogin: false,
    hidden: false,
    hideInMenu: frontEndConfig.minimizeMenu_qanda,
    left: { hidden: true },
    right: { hidden: true },
    contentRenderer: () => <QandAComingSoon />,
  };
};

const globalSearch = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  name: "Global Search",
  icon: "search",
  title: "application::search",
  route: "search",
  routeName: "search",
  noLogin: false,
  hidden: true,
  contentRenderer: () => <GlobalSearchResults />
})

const thePage = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 1,
  name: "Academy",
  icon: "info brain",
  route: "thepage",
  routeName: "thepage",
  // banner: "https://cdn.imaginarity.com/public/images/PAG_X.svg",
  title: "application::academy_board",
  noLogin: false,
  hidden: false,
  hideInMenu: frontEndConfig.minimizeMenu_thePage,
  contentRenderer: () => <ThePage />,

})

const goToExternal = (frontEndConfig: Record<string, any>): ThisAppEntry => ({
  startPriorityWeight: 1,
  name: "GoToExternal",
  icon: "chevron down",
  route: "goToExternal",
  routeName: "goToExternal",
  hidden: true,
  hideInMenu: frontEndConfig.minimizeMenu_goToExternal,
  contentRenderer: () => <GoToExternal />
})

const session = (): ThisAppEntry => ({
  startPriorityWeight: 1,
  name: "SessionManagement",
  icon: "user edit",
  title: "application::sessionManagement",
  route: "session",
  routeName: "session",
  noLogin: false,
  hidden: false,
  contentRenderer: () => <SessionLearnerOverview />,
  actions: [
    Actions.setAdd(() => gotoNamedRoute("session_new"))
  ],
  views: [
    {
      route: "/enrollTo/:id",
      routeName: "session_enroll_to",
      render: () => <SessionLearnerEnrollment />
    },
    {
      route: "/admin",
      routeName: "session_admin_overview",
      render: () => <SessionAdminOverview />
    },
    {
      route: "/admin/:id/enrollments",
      routeName: "session_admin_enrollments",
      render: () => <SessionAdminEnrollments />
    },
    {
      route: "/admin/new/session",
      routeName: "session_new",
      render: () => <SessionAdminCreateEdit type="Session" />
    },
    {
      route: "/admin/edit/session/:id",
      routeName: "session_edit",
      render: () => <SessionAdminCreateEdit type="Session" />
    },
    {
      route: "/admin/add/session/to/:templateId",
      routeName: "session_add",
      render: () => <SessionAdminCreateEdit type="Session" />
    },
    {
      route: "/admin/new/sessionTemplate",
      routeName: "session_template_new",
      render: () => <SessionAdminCreateEdit type="Template" />
    },
    {
      route: "/admin/edit/sessionTemplate/:templateId",
      routeName: "session_template_edit",
      render: () => <SessionAdminCreateEdit type="Template" />
    },
    {
      route: "/admin/sessionTypes",
      routeName: "sessionTypes",
      render: () => <SessionAdminSessionType />
    },
    {
      route: "/admin/resourceTypes",
      routeName: "sessionResourceTypes",
      render: () => <SessionAdminResourceType />
    },
    {
      route: "/admin/resources",
      routeName: "sessionResources",
      render: () => <SessionAdminResources />,
    },
    {
      route: "/admin/informations",
      routeName: "sessionInformations",
      render: () => <SessionAdminInformation />
    },
    {
      route: "/admin/roles",
      routeName: "session_roles",
      render: () => <RolesView groupTypes={["SESSION"]}
        roleRouteName="session_role"
        breadcrumpBaseRouteName="session_roles"
        breadcrumpBaseCaption="session"
        translationNamespace="session" />,

    },
    {
      route: "/admin/role/:groupId?/:id?",
      routeName: "session_role",
      render: () => <RoleView rolesRouteName="session_roles" />
    }
  ]
})


export const UpdateRequired = () => {
  const update = store.getState()?.requestUpdate;
  const { t } = useImgI18N("general");
  return (
    <div className="backdrop" style={{ display: update ? "block" : "none" }}>
      <button onClick={() => window.location.reload()}>
        {t("update is available. please close all tabs and reload.")}
      </button>
    </div>
  )
}

class App extends React.Component<AppProps, AppState> {
  public state: AppState = {
    colors: false,
    progress: 0,
    msg: "",
    loadingDone: x,
    showInfo: true
  };
  private startRoute: string | undefined = undefined;

  async componentDidMount() {
    // eslint-disable-next-line
    const getUrl = /^(?:https?:\/\/)?(?:[^@\/\n]+@)?(?:www\.)?([^:\/?\n]+)/;
    const url = (getUrl.exec(document.location.href) ?? ["", "localhost"])[0].toLowerCase();
    //const url = "https://ppx-clone";

    const f = await ControllerHelper.singleCall({ url }, DefaultController.GetFeaturesV2, true);
    const config = f?.frontEndConfig ?? {};
    const entries: Array<ThisAppEntry | AppEntry> = [
      FallbackAppEntry,
      ResetPasswordAppEntry,
      SsoAuthenticationFailedEntry,
      HistoryViewAppEntry
    ]
    if (f) {
      if (f.modules.myHome)
        entries.push(myhome(config));
      if (f.modules.playlist)
        entries.push(playlist(config));
      if (f.modules.myHome)
        entries.push(dataPrivacy(config));

      if (f.modules.academy)
        entries.push(academy(config));

      if (f.modules.pcnaMobileAcademy)
        entries.push(pcnaMobileAcademy(config));

      if (f.modules.feed) {
        // entries.push(appDownload());
        entries.push(feed(config));
      }
      if (f.modules.cms) {
        entries.push(cms(config));
        entries.push(cmscontent(config));
      }
      if (f.modules.wiki) {
        entries.push(wiki({
          groupAttribute: "wiki"
        }, config));
        entries.push(wiki({
          groupAttribute: "knowledge",
          backgroundImage: wikiTheme.infoHubAdminBackgroundUrl,
          applicationName: "infohub admin",
          baseRoute: "infohub_admin",
          icon: "wiki",
        }, config))
        entries.push(wiki({
          groupAttribute: "knowledge_support",
          backgroundImage: wikiTheme.infoHubSupportBackgroundUrl,
          applicationName: "infohub support",
          baseRoute: "infohub_support",
          icon: "wiki",
        }, config))
        entries.push(wiki({
          groupAttribute: "knowledge_learner",
          applicationName: "infohub learner",
          baseRoute: "infohub_learner",
          icon: "wiki",
        }, config))
        entries.push(wiki({
          groupAttribute: "warrenty",
          backgroundImage: wikiTheme.warrantyBackgroundUrl,
          applicationName: "warranty",
          baseRoute: "warranty",
          icon: "wiki",
        }, config))
      }
      if (f.modules.smirco)
        entries.push(smirco(config));






      if (f.modules.content)
        entries.push(content(config));
      if (f.modules.tube)
        entries.push(tube(config));
      if (f.modules.tube)
        entries.push(podcast(config));



      // TOFIX!!

      if (f.modules.quiz)
        entries.push(quiz(config));
      if (f.modules.chat)
        entries.push(chat(config));
      if (f.modules.faq)
        entries.push(faq(config));
      if (f.modules.user)
        entries.push(user(config));
      // if (f.modules.playlist)
      //   entries.push(playlist());
      if (f.modules.globalSearch)
        entries.push(globalSearch(config));
      if (f.modules.welcome)
        entries.push(welcome(config));
      if (f.modules.support)
        entries.push(support(config));
      if (f.modules.greeting)
        entries.push(greeting(config));
      if (f.modules.termsOfUse)
        entries.push(termsOfUse(config));
      if (f.modules.joinView)
        entries.push(joinView(config));
      if (f.modules.admin)
        entries.push(admin(config));


      if (f.modules.scorm)
        entries.push(scorm(config));
      if (f.modules.goToExternal)
        entries.push(goToExternal(config));
      if (f.modules.demos)
        entries.push({
          ...demos(config),
          noLogin: false,
        });
      //if (f.modules.bestPartices)
      entries.push(best(config));
      if (f.modules.qAndA && showNewQandAMenuGroup) {
        QANDA_MODELS
          .filter(m => m.published || showUnpublishedQandAInMenu)
          .forEach(m => entries.push(qandaModelEntry(m, config)));
      }
      else if (f.modules.qAndA) {
        entries.push(qanda({

          // groupAttribute: "knowledge_learner",
          // applicationName: "infohub learner",
          // baseRoute: "infohub_learner_1",
          // icon: "wiki",
          backgroundImage: wikiTheme.faqBackgroundUrl,
          groupAttribute: "qanda",

          //applicationName: "infohub learner",
          //baseRoute: "infohub_learner",
          //icon: "wiki",
        }, config));

        // entries.push(qanda({

        //   // groupAttribute: "knowledge_learner",
        //   // applicationName: "infohub learner",
        //   // baseRoute: "infohub_learner_1",
        //   // icon: "wiki",
        //   backgroundImage: wikiTheme.faqBackgroundUrl,
        //   groupAttribute: "qanda",
        //   baseRoute: "qqqqq",

        //   //applicationName: "infohub learner",
        //   //baseRoute: "infohub_learner",
        //   //icon: "wiki",
        // }, config));

      }
      // if (f.modules.test)
      //   entries.push(test(this));
      if (f.modules.slideShow)
        entries.push(slide(config));
      // if (f.modules.activities)
      //   entries.push(activities(this));
      if (f.modules.thePage)
        entries.push(thePage(config));
      entries.push(openPost(config));
      if (f.modules.inbox)
        entries.push(inbox(config));
      if (f.modules.session)
        entries.push(session());

    }

    // put in new entries if needed
    if (window.location.hostname === 'localhost') {
      entries.push(...[
        peTest(config),
        assetManagement(config),
        pagStats(config)
      ])
    }

    //console.log("App.tsx::1909 => f", f);

    _.forEach(f?.applicationSettings, (settings, appName) => {
      const entry = _.find(entries, e => e.name?.toLocaleLowerCase() === appName.toLocaleLowerCase());
      if (entry) {
        entry.order = settings.tabOrder;
      }
    });

    await ImgI18N.getInstance().initialize();


    const pluginRegistry = PluginRegistry.getInstance();
    //pluginRegistry.addPluginToLoad("porscheicons", true);
    //pluginRegistry.addPluginToLoad("quiz", true);
    await pluginRegistry.loadPlugins({
      icons: imgIconDefs,
      store
    });
    setTimeout(() => this.setState({ loadingDone: true }), 0);

    store.dispatch(PluginActions.batchActions([
      PluginActions.addAppEntries(entries as AppEntry[]),
      PluginActions.addModules(a1),
      PluginActions.setPlatformFeatures(f),
    ]))

    // store.dispatch(PluginActions.addAppEntries(entries));
    // store.dispatch(PluginActions.addModules(a1));
    // store.dispatch(PluginActions.setPlatformFeatures(f));
    //this.startRoute = "/graph";
    if (MobileHelper.getInstance().mobileClient())
      this.startRoute = "/community";

  }
  public render() {

    //console.log("Mobile => ", MobileHelper.getInstance().mobileClient());

    // If you want your app to work offline and load faster, you can change
    // unregister() to register() below. Note this comes with some pitfalls.
    // Learn more about service workers: https://bit.ly/CRA-PWA
    register({
      onSuccess: registration => {
        console.log("registered app for offline use. details:", registration)
      },
      onUpdate: registration => {
        //const waitingServiceWorker = registration.waiting

        store.dispatch(Actions.requestUpdate(true));
        console.log("Update called to check updates");

        // if (waitingServiceWorker) {
        //   waitingServiceWorker.addEventListener("statechange", (event: any) => {
        //     if (event.target.state === "activated") {
        //       store.dispatch(Actions.requestUpdate(true))
        //     }
        //   })
        // }
      },
    })



    // console.log(applicationTheme);
    const isIE = (getBrowser().name === "ie");
    //console.log('this.startRoute => ', this.startRoute);
    //console.log('maxApp.route => ', maxApp?.route);
    return (
      <>
        <UpdateRequired />

        <Provider store={store}>
          <HandleAppFocus />
          <ImgThemeProvider theme={applicationTheme}>
            <Suspense fallback={null}>
              {this.state.loadingDone ?
                <MasterLayoutRouter startRoute={this.startRoute} previewRender={() => <AppStart progress={this.state.progress} msg={this.state.msg} />}>
                  <Div100vh>
                    <MasterLayout
                      //leftModules={["ContentInteraction", "SystemInfo"]}
                      //rightModules={["SearchAndFilter", "ContentInfo", "ContentContext", "UserInteraction"]}
                      leftHeader={<Search />}
                      rightHeader={<UserStatus />}
                    />
                  </Div100vh>
                </MasterLayoutRouter>
                :
                <AppStart progress={this.state.progress} msg={this.state.msg} />
              }

              <GlobalStyle />
              {
                isIE && this.state.showInfo &&
                <IEWarning onClick={this.hideInfo}>
                  We suggest to use a modern Browser - IE 11 is outdated and this site will look better in modern browsers.
                </IEWarning>
              }
              <PageStateConnection />
            </Suspense>
          </ImgThemeProvider>
        </Provider >
      </>
    );
  }

  public hideInfo = () => {
    this.setState({ showInfo: false });
  }
  public toggleColors = () => {
    this.setState({ colors: !this.state.colors });
  }
}

export default App;
